<template>
  <div
    class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]"
  >
    <section class="flex flex-col items-center w-full justify-center">
      <div class="w-full mx-auto">
        <div class="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6 min-h-[80vh]">
          <!-- Cache status indicator -->
          <!-- <div v-if="hasData" class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-600">Generated: {{ lastGeneratedAt }}</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small color="info" v-on="on">mdi-information-outline</v-icon>
                </template>
                <span>Data is cached for 24 hours. Next refresh: {{ formatCacheExpiration() }}</span>
              </v-tooltip>
              <v-chip 
                v-if="isBackgroundRefreshing" 
                x-small 
                color="warning" 
                class="px-2"
              >
                <v-progress-circular
                  indeterminate
                  size="12"
                  width="2"
                  class="mr-2"
                  color="white"
                ></v-progress-circular>
                Refreshing...
              </v-chip>
            </div>
            <v-btn 
              x-small 
              text 
              color="primary" 
              @click="forceReloadData"
              :disabled="isGenerating || isBackgroundRefreshing"
            >
              Refresh Now
            </v-btn>
          </div> -->

          <!-- Existing filter and button section -->
          <div class="flex flex-col gap-4">
            <div
              class="flex flex-col sm:flex-row md:items-center justify-between gap-4"
            >
              <div
                class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
              >
                <div class="w-full sm:w-auto">
                  <div class="location-dropdown relative md:max-w-[200px]">
                    <v-btn
                      @click="toggleStoreDropdown($event)"
                      outlined
                      color="primary"
                    >
                      {{ selectedWIDs?.length || 0 }} Selected Locations
                    </v-btn>
                    <LocationDropdown
                      ref="locationDropdown"
                      :stores="filteredStores"
                      :reviewReport="true"
                      :showStoreDropdown="showStoreDropdown"
                      :totalAverageRating="String(totalAverageRating)"
                      v-model="selectedWIDs"
                      @change="handleLocationChange"
                    />
                  </div>
                </div>
                <div class="w-full sm:w-auto">
                  <v-select
                    v-model="selectedRatingRange"
                    :items="ratingRanges"
                    label="Filter by Locations Rating"
                    class="rating-filter mt-2"
                    dense
                    :disabled="isGenerating"
                    outlined
                  />
                </div>
                <!-- Add Month Filter -->
                <div class="w-full sm:w-auto">
                  <v-select
                    v-model="selectedMonth"
                    :items="availableMonths"
                    label="Filter by Month"
                    class="month-filter mt-2"
                    dense
                    :disabled="isGenerating"
                    outlined
                    clearable
                    @change="handleMonthChange"
                  />
                </div>
                <div
                  v-if="isGenerating"
                  class="flex items-center text-sm bg-blue-50 text-blue-700 px-4 py-2 rounded-md border border-blue-200"
                >
                  <v-progress-circular
                    indeterminate
                    size="16"
                    width="2"
                    class="mr-2"
                    color="primary"
                  ></v-progress-circular>
                  Generating report...
                </div>
                <div
                  v-else-if="lastGeneratedAt"
                  class="text-sm text-gray-500 flex items-center whitespace-nowrap"
                >
                  <span>Generated {{ lastGeneratedAt }}</span>
                </div>
              </div>
              <div class="flex gap-2 ml-auto">
                <v-menu
                  v-model="showContextMenu"
                  :position-x="menuX"
                  :position-y="menuY"
                  absolute
                  offset-y
                >
                  <template v-slot:activator="{}">
                    <v-btn
                      color="primary"
                      outlined
                      @click="forceReloadData"
                      @contextmenu.prevent="
                        showContextMenu = true;
                        $event.stopPropagation();
                        menuX = $event.clientX;
                        menuY = $event.clientY;
                      "
                      :loading="isGenerating"
                      class="h-[40px]"
                    >
                      <v-icon left>mdi-refresh</v-icon> Refresh Data
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="clearCache">
                      <v-list-item-icon>
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Clear Cache</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn
                  v-if="hasData"
                  color="secondary"
                  @click="downloadReport"
                  :loading="isDownloading"
                  class="h-[40px]"
                >
                  <v-icon left>mdi-download</v-icon> Download Report
                </v-btn>
              </div>
            </div>

            <!-- Remove the old Download Report button row since we moved it up -->
            <div
              v-if="hasData"
              class="flex flex-col sm:flex-row gap-4 py-4 border-t border-gray-200 hidden"
            >
              <v-btn
                color="secondary"
                @click="downloadReport"
                :loading="isDownloading"
                class="px-6 h-[40px] w-full sm:w-auto"
              >
                <v-icon left>mdi-download</v-icon> Download Report
              </v-btn>
              <div
                v-if="lastGeneratedAt"
                class="text-center flex items-center justify-end"
              >
                <p class="text-sm text-gray-500">
                  Generated {{ lastGeneratedAt }}
                </p>
              </div>
            </div>
          </div>

          <!-- Selected Locations Display -->
          <div
            v-if="
              hasData &&
              selectedWIDs.length > 0 &&
              selectedWIDs.length < stores.length
            "
            class="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200"
          >
            <div class="flex justify-between items-center mb-2">
              <h3 class="text-sm font-medium text-gray-700">
                Selected Locations ({{ selectedWIDs.length }})
              </h3>
              <button
                @click="showAllLocations = !showAllLocations"
                class="text-sm text-blue-600 hover:text-blue-800"
              >
                {{ showAllLocations ? "Show Less" : "Show All" }}
              </button>
            </div>
            <div class="flex flex-wrap gap-2">
              <v-chip
                v-for="(locationId, index) in showAllLocations
                  ? selectedWIDs
                  : selectedWIDs.slice(0, 5)"
                :key="index"
                small
                outlined
                class="text-xs"
              >
                {{ findLocationName(locationId) }}
              </v-chip>
              <v-chip
                v-if="!showAllLocations && selectedWIDs.length > 5"
                small
                outlined
                class="text-xs"
              >
                +{{ selectedWIDs.length - 5 }} more
              </v-chip>
            </div>
          </div>

          <!-- Brand Perception Widget (moved to top) -->
          <div
            v-if="hasData"
            class="bg-white rounded-lg shadow-sm mb-6 mt-6 overflow-hidden"
          >
            <div
              class="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200"
            >
              <h2 class="text-lg font-medium text-gray-800">
                Brand Perception
              </h2>
            </div>

            <div class="p-4">
              <div class="flex flex-col md:flex-row items-center gap-6">
                <!-- Sentiment Score -->
                <div class="w-full md:w-1/3 flex flex-col items-center">
                  <div class="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" class="w-full h-full">
                      <!-- Background Circle -->
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#E5E7EB"
                        stroke-width="10"
                      />

                      <!-- Sentiment Arc -->
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        :stroke="getSentimentColor(calculateBrandSentiment())"
                        stroke-width="10"
                        :stroke-dasharray="`${calculateSentimentArc()} ${
                          283 - calculateSentimentArc()
                        }`"
                        stroke-dashoffset="70"
                        stroke-linecap="round"
                      />

                      <!-- Score Text -->
                      <text
                        x="50"
                        y="45"
                        text-anchor="middle"
                        font-size="18"
                        font-weight="bold"
                        fill="#374151"
                      >
                        {{ calculateBrandSentiment().toFixed(1) }}
                      </text>
                      <text
                        x="50"
                        y="65"
                        text-anchor="middle"
                        font-size="10"
                        fill="#6B7280"
                      >
                        Sentiment Score
                      </text>
                    </svg>
                  </div>
                </div>

                <!-- Brand Insights Summary -->
                <div class="w-full md:w-2/3">
                  <h3 class="text-base font-medium text-gray-700 mb-2">
                    Overall Summary
                  </h3>
                  <p class="text-sm text-gray-600 mb-4">
                    {{ generateBrandSummary() }}
                  </p>

                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <h4
                        class="text-sm font-medium text-green-700 mb-2 flex items-center"
                      >
                        <v-icon small class="mr-1">mdi-thumb-up</v-icon> Top
                        Strengths
                      </h4>
                      <div class="flex flex-wrap gap-2">
                        <v-chip
                          v-for="(keyword, idx) in topPositiveKeywords.slice(
                            0,
                            3
                          )"
                          :key="idx"
                          small
                          label
                          class="bg-green-50 text-green-700 cursor-pointer"
                          @click="showLocationsForKeyword(keyword)"
                        >
                          {{ keyword.text }} ({{ keyword.count }})
                        </v-chip>
                        <span
                          v-if="topPositiveKeywords.length === 0"
                          class="text-sm text-gray-500"
                        >
                          No strengths data available
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4
                        class="text-sm font-medium text-red-700 mb-2 flex items-center"
                      >
                        <v-icon small class="mr-1">mdi-thumb-down</v-icon> Top
                        Concerns
                      </h4>
                      <div class="flex flex-wrap gap-2">
                        <v-chip
                          v-for="(keyword, idx) in topNegativeKeywords.slice(
                            0,
                            3
                          )"
                          :key="idx"
                          small
                          label
                          class="bg-red-50 text-red-700 cursor-pointer"
                          @click="showLocationsForKeyword(keyword)"
                        >
                          {{ keyword.text }} ({{ keyword.count }})
                        </v-chip>
                        <span
                          v-if="topNegativeKeywords.length === 0"
                          class="text-sm text-gray-500"
                        >
                          No concerns data available
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Staff Mentions Section -->
                  <div v-if="topStaffMentions.length > 0" class="mt-4">
                    <h4
                      class="text-sm font-medium text-blue-700 mb-2 flex items-center"
                    >
                      <v-icon small class="mr-1">mdi-account-star</v-icon> Top
                      Staff Mentions
                    </h4>
                    <div class="flex flex-wrap gap-2">
                      <v-chip
                        v-for="(staff, idx) in topStaffMentions.slice(0, 5)"
                        :key="idx"
                        small
                        label
                        class="bg-blue-50 text-blue-700 cursor-pointer"
                        @click="showLocationsForStaffMember(staff)"
                      >
                        {{ staff.name }} ({{ staff.count }})
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Summary Section -->
          <div
            v-if="hasData"
            class="bg-white rounded-lg shadow-sm mb-6 overflow-hidden"
          >
            <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h2 class="text-lg font-medium text-gray-800">
                Insights Summary
              </h2>
            </div>

            <!-- Summary Cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
              <!-- Overall Rating Card -->
              <div class="bg-white border rounded-lg p-4 shadow-sm">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-medium text-gray-600">
                    Overall Rating
                    {{
                      selectedMonth !== "all"
                        ? "for " + formatMonth(selectedMonth)
                        : ""
                    }}
                  </h3>
                  <span
                    class="text-amber-500 flex items-center text-lg font-bold"
                  >
                    {{ calculateOverallRating().toFixed(1) }}
                    <v-icon small color="amber" class="ml-1">mdi-star</v-icon>
                  </span>
                </div>
                <div class="mt-2">
                  <div
                    class="flex items-center justify-between text-xs text-gray-500 mb-1"
                  >
                    <span>Rating Distribution</span>
                    <span>{{ calculateTotalReviews() }} reviews</span>
                  </div>
                  <div class="w-full">
                    <div
                      v-for="star in 5"
                      :key="star"
                      class="flex items-center mb-1"
                    >
                      <div class="w-8 text-xs text-right pr-2">
                        {{ 6 - star }}★
                      </div>
                      <div class="w-full bg-gray-200 rounded-full h-2">
                        <div
                          class="bg-amber-400 h-2 rounded-full"
                          :style="`width: ${calculateRatingPercentage(
                            6 - star
                          )}%`"
                        ></div>
                      </div>
                      <div class="w-12 text-xs pl-2">
                        {{ calculateRatingCount(6 - star) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Trend Card -->
              <div class="bg-white border rounded-lg p-4 shadow-sm">
                <h3 class="text-sm font-medium text-gray-600 mb-2">
                  Rating Trend
                </h3>
                <div class="h-32 rating-trend">
                  <svg width="100%" height="100%" viewBox="0 0 300 100">
                    <!-- Background grid lines -->
                    <g class="grid-lines" stroke="#E5E7EB" stroke-width="1">
                      <line x1="10" y1="20" x2="290" y2="20" />
                      <line x1="10" y1="40" x2="290" y2="40" />
                      <line x1="10" y1="60" x2="290" y2="60" />
                      <line x1="10" y1="80" x2="290" y2="80" />
                    </g>

                    <!-- Rating scale -->
                    <g class="rating-scale" fill="#6B7280" font-size="10">
                      <text x="5" y="23" text-anchor="end">5★</text>
                      <text x="5" y="43" text-anchor="end">4★</text>
                      <text x="5" y="63" text-anchor="end">3★</text>
                      <text x="5" y="83" text-anchor="end">2★</text>
                    </g>

                    <!-- Trend line -->
                    <polyline
                      :points="ratingTrendPoints"
                      fill="none"
                      stroke="#4F46E5"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                    <!-- Data points -->
                    <g v-for="(point, index) in ratingTrendData" :key="index">
                      <circle
                        :cx="
                          index * (280 / (ratingTrendData.length - 1 || 1)) + 10
                        "
                        :cy="100 - point * 15"
                        r="4"
                        fill="#4F46E5"
                      />
                      <text
                        :x="
                          index * (280 / (ratingTrendData.length - 1 || 1)) + 10
                        "
                        :y="100 - point * 15 - 10"
                        text-anchor="middle"
                        font-size="10"
                        fill="#4B5563"
                      >
                        {{ point.toFixed(1) }}
                      </text>
                    </g>
                  </svg>
                </div>
                <div class="flex justify-between text-xs text-gray-500 mt-2">
                  <span
                    v-for="(month, index) in Object.keys(activeData)
                      .sort((a, b) => new Date(a) - new Date(b))
                      .slice(-6)"
                    :key="index"
                  >
                    {{ formatMonthAbbrev(month) }}
                  </span>
                </div>
              </div>

              <!-- Top Keywords Card -->
              <div class="bg-white border rounded-lg p-4 shadow-sm">
                <h3 class="text-sm font-medium text-gray-600 mb-2">
                  Top Insights
                </h3>
                <div class="space-y-2">
                  <div>
                    <h4 class="text-xs font-medium text-green-700 mb-1">
                      Top Positives
                    </h4>
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="(keyword, idx) in topPositiveKeywords"
                        :key="idx"
                        class="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 cursor-pointer"
                        @click="showLocationsForKeyword(keyword)"
                      >
                        {{ keyword.text }} ({{ keyword.count }})
                      </span>
                      <span
                        v-if="topPositiveKeywords.length === 0"
                        class="text-xs text-gray-500"
                      >
                        No positive insights available
                      </span>
                    </div>
                  </div>
                  <div>
                    <h4 class="text-xs font-medium text-red-700 mb-1">
                      Top Concerns
                    </h4>
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="(keyword, idx) in topNegativeKeywords"
                        :key="idx"
                        class="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 cursor-pointer"
                        @click="showLocationsForKeyword(keyword)"
                      >
                        {{ keyword.text }} ({{ keyword.count }})
                      </span>
                      <span
                        v-if="topNegativeKeywords.length === 0"
                        class="text-xs text-gray-500"
                      >
                        No negative insights available
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Monthly Reviews & Rating Trends Table -->
          <div
            v-if="hasData"
            class="bg-white rounded-lg shadow-sm mb-6 overflow-hidden"
          >
            <div
              class="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center"
            >
              <h2 class="text-lg font-medium text-gray-800">
                Monthly Reviews & Rating Trends
              </h2>
            </div>

            <div class="p-4">
              <div v-if="isTableLoading" class="flex justify-center py-6">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
              </div>

              <div
                v-else-if="monthlyTableData.length === 0"
                class="py-8 text-center text-gray-500"
              >
                <p>No monthly data available for the selected time range.</p>
                <p class="mt-2 text-sm">
                  Try selecting a different time range or refreshing the data.
                </p>
              </div>

              <div v-else class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Month
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Reviews&nbsp;
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Avg. Rating
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        5★
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        4★
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        3★
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        2★
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        1★
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        With Text
                      </th>
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Replied
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(month, index) in monthlyTableData"
                      :key="index"
                      class="hover:bg-gray-50"
                    >
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900"
                      >
                        {{ formatMonth(month.monthKey) }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.totalReviews }}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap">
                        <div class="flex items-center">
                          <span
                            class="text-sm font-medium text-gray-900 mr-2"
                            >{{ month.averageRating.toFixed(1) }}</span
                          >
                          <div class="flex">
                            <v-icon
                              v-for="n in 5"
                              :key="n"
                              small
                              :color="
                                n <= Math.round(month.averageRating)
                                  ? 'amber'
                                  : 'grey lighten-2'
                              "
                            >
                              mdi-star
                            </v-icon>
                          </div>
                        </div>
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.ratings.FIVE || 0 }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.ratings.FOUR || 0 }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.ratings.THREE || 0 }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.ratings.TWO || 0 }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.ratings.ONE || 0 }}
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.textReviews }}
                        <span v-if="month.totalReviews > 0">
                          ({{
                            Math.round(
                              (month.textReviews / month.totalReviews) * 100
                            )
                          }}%)
                        </span>
                      </td>
                      <td
                        class="px-3 py-2 whitespace-nowrap text-sm text-gray-500"
                      >
                        {{ month.repliedReviews }}
                        <span v-if="month.totalReviews > 0">
                          ({{
                            Math.round(
                              (month.repliedReviews / month.totalReviews) * 100
                            )
                          }}%)
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Report Data -->
          <div v-if="hasData" class="space-y-6">
            <!-- Desktop Table -->
            <div class="bg-white rounded-lg shadow-sm overflow-hidden">
              <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <h2 class="text-lg font-medium text-gray-800">
                  Feedback Summary
                  {{
                    selectedMonth === "all"
                      ? "by Month"
                      : "for " + formatMonth(selectedMonth)
                  }}
                </h2>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 table-report">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Month
                      </th>
                      <th
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Total Reviews
                      </th>
                      <th
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Average Rating
                      </th>
                      <th
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Positive Feedback
                      </th>
                      <th
                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Negative Feedback
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <template v-for="(data, month) in activeData">
                      <tr :key="month">
                        <td
                          class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                          {{ formatMonth(month) }}
                        </td>
                        <td
                          class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {{ data.aggregated?.totalReviews }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <div class="flex items-center">
                            <span
                              class="text-sm font-medium text-gray-900 mr-2"
                            >
                              {{
                                calculateAverageRating(
                                  data.aggregated.ratingDistribution
                                ).toFixed(1)
                              }}
                            </span>
                            <v-icon
                              v-for="i in 5"
                              :key="i"
                              small
                              :color="
                                i <=
                                calculateAverageRating(
                                  data.aggregated.ratingDistribution
                                )
                                  ? 'amber'
                                  : 'grey lighten-2'
                              "
                            >
                              mdi-star
                            </v-icon>
                          </div>
                        </td>
                        <td class="px-6 py-4">
                          <ul
                            v-if="
                              data.aggregated?.feedback?.positive?.length > 0
                            "
                            class="list-disc pl-4 text-sm text-green-600 space-y-1 max-w-xs"
                          >
                            <li
                              v-for="(point, index) in limitFeedback(
                                data.aggregated?.feedback?.positive
                              )"
                              :key="index"
                            >
                              {{ point }}
                            </li>
                            <li
                              v-if="
                                data.aggregated?.feedback?.positive?.length > 5
                              "
                              class="cursor-pointer text-blue-600 mt-2 list-none"
                            >
                              <span
                                @click="
                                  showAllFeedback(
                                    data.aggregated?.feedback?.positive,
                                    'positive',
                                    month
                                  )
                                "
                              >
                                Show
                                {{
                                  data.aggregated?.feedback?.positive?.length -
                                  5
                                }}
                                more...
                              </span>
                            </li>
                          </ul>
                          <span v-else class="text-sm text-gray-500"
                            >No positive feedback</span
                          >
                        </td>
                        <td class="px-6 py-4">
                          <ul
                            v-if="
                              data.aggregated?.feedback?.negative?.length > 0
                            "
                            class="list-disc pl-4 text-sm text-red-600 space-y-1 max-w-xs"
                          >
                            <li
                              v-for="(point, index) in limitFeedback(
                                data.aggregated?.feedback?.negative
                              )"
                              :key="index"
                            >
                              {{ point }}
                            </li>
                            <li
                              v-if="
                                data.aggregated?.feedback?.negative?.length > 5
                              "
                              class="cursor-pointer text-blue-600 mt-2 list-none"
                            >
                              <span
                                @click="
                                  showAllFeedback(
                                    data.aggregated?.feedback?.negative,
                                    'negative',
                                    month
                                  )
                                "
                              >
                                Show
                                {{
                                  data.aggregated?.feedback?.negative?.length -
                                  5
                                }}
                                more...
                              </span>
                            </li>
                          </ul>
                          <span v-else class="text-sm text-gray-500"
                            >No negative feedback</span
                          >
                        </td>
                      </tr>
                      <tr
                        :key="`${month}-keywords`"
                        v-if="
                          data.aggregated.keywords.length > 0 ||
                          data.aggregated.stf.length > 0
                        "
                      >
                        <td colspan="5" class="px-6 py-4 bg-gray-50">
                          <div class="flex flex-wrap gap-2 items-center">
                            <span class="text-sm font-medium text-gray-600 mr-2"
                              >Key Insights:</span
                            >
                            <template
                              v-for="type in [
                                'positive',
                                'neutral',
                                'negative',
                              ]"
                            >
                              <span
                                v-for="(kw, idx) in limitKeywords(
                                  data.aggregated.keywords.filter(
                                    (k) => k.type === type
                                  )
                                )"
                                :key="`${type}-${idx}`"
                                :class="[
                                  'inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer',
                                  getKeywordClass(kw.type),
                                ]"
                                @click="showLocationsForKeyword(kw, month)"
                              >
                                {{ kw.text }} ({{ kw.count }})
                              </span>
                            </template>
                            <span
                              v-if="data.aggregated.keywords.length > 10"
                              class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-gray-100 text-gray-800"
                              @click="
                                showAllKeywords(data.aggregated.keywords, month)
                              "
                            >
                              Show
                              {{ data.aggregated.keywords.length - 10 }} more...
                            </span>
                            <!-- Staff Mentions in monthly table - Start on new line -->
                          </div>
                          <template v-if="data.aggregated.stf.length > 0">
                              <br />
                              <div class="mt-0">
                                <span
                                  class="text-sm font-medium text-blue-600"
                                >
                                  Staff Mentions:
                                </span>
                                <span
                                  v-for="(
                                    staff, idx
                                  ) in [...data.aggregated.stf].sort((a, b) => b.count - a.count).slice(0, 5)"
                                  :key="`staff-${idx}`"
                                  class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-blue-100 text-blue-800 ml-2"
                                  @click="showLocationsForStaffMember(staff, month)"
                                >
                                  {{ staff.name }} ({{ staff.count }})
                                </span>
                                <span
                                  v-if="data.aggregated.stf.length > 5"
                                  class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-gray-100 text-gray-800 ml-2"
                                  @click="showAllStaffMentions([...data.aggregated.stf].sort((a, b) => b.count - a.count), month)"
                                >
                                  +{{ data.aggregated.stf.length - 5 }} more
                                </span>
                              </div>
                          </template>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Mobile View - Update mobile cards with limited feedback and keywords -->
            <div v-if="reportData?.data" class="md:hidden space-y-6">
              <div
                v-for="(data, month) in reportData.data"
                :key="month"
                class="bg-white rounded-lg shadow-sm p-4"
              >
                <div class="mb-4">
                  <h3 class="text-lg font-semibold text-gray-800">
                    {{ formatMonth(month) }}
                  </h3>
                  <div class="flex items-center gap-4 mt-2">
                    <!-- Existing header code -->
                  </div>
                </div>
                <div class="space-y-4">
                  <div class="p-3 bg-green-50 rounded-lg">
                    <div class="font-medium text-green-700 mb-2">
                      Positive Feedback
                    </div>
                    <ul
                      v-if="data.aggregated?.feedback?.positive?.length > 0"
                      class="list-disc pl-4 text-sm text-green-600 space-y-1"
                    >
                      <li
                        v-for="(point, index) in limitFeedback(
                          data.aggregated?.feedback?.positive
                        )"
                        :key="index"
                      >
                        {{ point }}
                      </li>
                      <li
                        v-if="data.aggregated?.feedback?.positive?.length > 5"
                        class="cursor-pointer text-blue-600 mt-2 list-none"
                      >
                        <span
                          @click="
                            showAllFeedback(
                              data.aggregated?.feedback?.positive,
                              'positive',
                              month
                            )
                          "
                        >
                          Show
                          {{ data.aggregated?.feedback?.positive?.length - 5 }}
                          more...
                        </span>
                      </li>
                    </ul>
                    <span v-else class="text-sm text-gray-500"
                      >No positive feedback</span
                    >
                  </div>
                  <div class="p-3 bg-red-50 rounded-lg">
                    <div class="font-medium text-red-700 mb-2">
                      Negative Feedback
                    </div>
                    <ul
                      v-if="data.aggregated?.feedback?.negative?.length > 0"
                      class="list-disc pl-4 text-sm text-red-600 space-y-1"
                    >
                      <li
                        v-for="(point, index) in limitFeedback(
                          data.aggregated?.feedback?.negative
                        )"
                        :key="index"
                      >
                        {{ point }}
                      </li>
                      <li
                        v-if="data.aggregated?.feedback?.negative?.length > 5"
                        class="cursor-pointer text-blue-600 mt-2 list-none"
                      >
                        <span
                          @click="
                            showAllFeedback(
                              data.aggregated?.feedback?.negative,
                              'negative',
                              month
                            )
                          "
                        >
                          Show
                          {{ data.aggregated?.feedback?.negative?.length - 5 }}
                          more...
                        </span>
                      </li>
                    </ul>
                    <span v-else class="text-sm text-gray-500"
                      >No negative feedback</span
                    >
                  </div>
                  <div class="p-3 bg-blue-50 rounded-lg">
                    <div class="font-medium text-blue-700 mb-2">Keywords</div>
                    <div class="flex flex-wrap gap-2">
                      <template
                        v-for="type in ['positive', 'neutral', 'negative']"
                      >
                        <span
                          v-for="(kw, idx) in limitKeywords(
                            data.aggregated.keywords.filter(
                              (k) => k.type === type
                            )
                          )"
                          :key="`${type}-${idx}`"
                          :class="[
                            'inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer',
                            getKeywordClass(kw.type),
                          ]"
                          @click="showLocationsForKeyword(kw, month)"
                        >
                          {{ kw.text }} ({{ kw.count }})
                        </span>
                      </template>
                      <span
                        v-if="data.aggregated.keywords.length > 10"
                        class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-gray-100 text-gray-800"
                        @click="
                          showAllKeywords(data.aggregated.keywords, month)
                        "
                      >
                        Show {{ data.aggregated.keywords.length - 10 }} more...
                      </span>
                    </div>
                  </div>

                  <!-- Staff Mentions in mobile view -->
                  <div
                    v-if="data.aggregated.stf.length > 0"
                    class="p-3 bg-blue-50 rounded-lg mt-4"
                  >
                    <div class="font-medium text-blue-700 mb-2">
                      Staff Mentions
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="(staff, idx) in [...data.aggregated.stf].sort((a, b) => b.count - a.count).slice(0, 5)"
                        :key="`staff-${idx}`"
                        class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-blue-100 text-blue-800"
                        @click="showLocationsForStaffMember(staff, month)"
                      >
                        {{ staff.name }} ({{ staff.count }})
                      </span>
                      <span
                        v-if="data.aggregated.stf.length > 5"
                        class="inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer bg-gray-100 text-gray-800"
                        @click="showAllStaffMentions([...data.aggregated.stf].sort((a, b) => b.count - a.count), month)"
                      >
                        +{{ data.aggregated.stf.length - 5 }} more
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Additional modals -->
    <v-dialog v-model="showAllKeywordsModal" max-width="600px">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            All Keywords - {{ selectedMonth ? formatMonth(selectedMonth) : "" }}
          </div>
          <v-btn icon @click="showAllKeywordsModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="flex flex-wrap gap-2">
            <template v-for="type in ['positive', 'neutral', 'negative']">
              <span
                v-for="(kw, idx) in (allKeywords &&
                  allKeywords.filter((k) => k.type === type)) ||
                []"
                :key="`${type}-${idx}`"
                :class="[
                  'inline-flex items-center px-2 py-1 rounded-full text-sm cursor-pointer mb-2',
                  getKeywordClass(kw.type),
                ]"
                @click="
                  showLocationsForKeyword(kw, selectedMonth);
                  showAllKeywordsModal = false;
                "
              >
                {{ kw.text }} ({{ kw.count }})
              </span>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Staff Mentions Modal (similar to Keywords Modal) -->
    <v-dialog v-model="showAllStaffMentionsModal" max-width="600px">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            All Staff Mentions - {{ selectedMonth ? formatMonth(selectedMonth) : "" }}
          </div>
          <v-btn icon @click="showAllStaffMentionsModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(staff, idx) in allStaffMentions"
              :key="idx"
              class="inline-flex items-center px-3 py-2 rounded-full text-sm cursor-pointer mb-2 bg-blue-100 text-blue-800 hover:bg-blue-200"
              @click="
                showLocationsForStaffMember(staff, selectedMonth);
                showAllStaffMentionsModal = false;
              "
            >
              {{ staff.name }} ({{ staff.count }})
            </span>
            <span
              v-if="allStaffMentions.length === 0"
              class="text-sm text-gray-500"
            >
              No staff mentions found
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAllFeedbackModal" max-width="600px">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            {{ feedbackType === "positive" ? "Positive" : "Negative" }} Feedback
            - {{ selectedMonth ? formatMonth(selectedMonth) : "" }}
          </div>
          <v-btn icon @click="showAllFeedbackModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <ul
            :class="[
              'list-disc pl-4 text-sm space-y-1 max-w-full',
              feedbackType === 'positive' ? 'text-green-600' : 'text-red-600',
            ]"
          >
            <li v-for="(point, index) in allFeedback" :key="index">
              {{ point }}
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Original location modal for a specific keyword -->
    <v-dialog v-model="showLocationModal" max-width="600px" scrollable @click:outside="closeLocationModal">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            Locations for "{{ selectedKeyword?.text }}"
          </div>
          <div class="text-caption text-grey-darken-1">
            ({{ filteredLocations.length }} locations)
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Search input with new variable name -->
          <v-text-field
            v-model="keywordLocationSearch"
            dense
            outlined
            placeholder="Search locations..."
            prepend-inner-icon="mdi-magnify"
            class="mb-4"
            hide-details
          ></v-text-field>

          <v-list dense class="location-list">
            <v-list-item
              v-for="(locationId, index) in paginatedLocations"
              :key="index"
              @click="fetchKeywordReviews(locationId, selectedKeyword)"
              class="cursor-pointer hover:bg-gray-50"
            >
              <v-list-item-content class="py-2">
                <div class="d-flex justify-space-between align-center gap-4">
                  <div class="flex-grow-1 min-w-0">
                    <div
                      class="text-subtitle-2 font-weight-medium break-words text-blue-600 hover:text-blue-800"
                    >
                      {{ findLocationName(locationId.id) }}
                    </div>
                    <div class="text-caption text-grey-darken-1 break-words">
                      {{ findLocationAddress(locationId.id) }}
                    </div>
                  </div>
                  <div
                    class="text-caption font-weight-medium whitespace-nowrap"
                  >
                    ({{ locationId.count }})
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="filteredLocations.length === 0">
              <v-list-item-content>
                <v-list-item-title class="text-sm text-gray-500 text-center">
                  {{
                    keywordLocationSearch
                      ? "No matching locations found"
                      : "No locations found"
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Pagination -->
          <div class="d-flex justify-center mt-4">
            <v-pagination
              v-if="filteredLocations.length > itemsPerPage"
              v-model="currentPage"
              :length="Math.ceil(filteredLocations.length / itemsPerPage)"
              :total-visible="5"
            ></v-pagination>
          </div>
        </v-card-text>

        <v-card-actions class="sticky bottom-0 bg-white border-t">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeLocationModal"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Keyword Reviews Modal -->
    <v-dialog v-model="showKeywordReviewsModal" max-width="800px" scrollable @click:outside="closeKeywordReviewsModal">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            Reviews for "{{ selectedKeywordForReviews?.text }}" at
            {{ selectedLocationName }}
          </div>
          <div class="text-caption text-grey-darken-1">
            ({{ keywordReviews.length }} reviews)
          </div>
        </v-card-title>

        <v-card-text class="pt-0">
          <!-- Add sorting controls -->
          <div
            class="mb-4 flex items-center gap-4 sticky top-0 bg-white z-10 py-4 border-b"
          >
            <v-select
              v-model="reviewsSortField"
              :items="[
                { text: 'Date', value: 'createTime' },
                { text: 'Rating', value: 'starRating' },
              ]"
              label="Sort by"
              dense
              outlined
              hide-details
              class="w-32"
            />
            <v-btn-toggle v-model="reviewsSortOrder" mandatory>
              <v-btn small value="asc">
                <v-icon>mdi-sort-ascending</v-icon>
              </v-btn>
              <v-btn small value="desc">
                <v-icon>mdi-sort-descending</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-select
              v-model="reviewsPerPage"
              :items="[5, 10, 20, 50, { text: 'All', value: -1 }]"
              label="Reviews per page"
              dense
              outlined
              hide-details
              class="w-32"
            />
          </div>

          <div v-if="isLoadingKeywordReviews" class="text-center py-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <p class="mt-4 text-gray-600">Loading reviews...</p>
          </div>

          <div v-else-if="keywordReviews.length === 0" class="text-center py-6">
            <p class="text-gray-500">
              No reviews found for this keyword and location.
            </p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="(review, idx) in paginatedAndSortedReviews"
              :key="review.reviewId || idx"
              class="border border-gray-200 rounded-lg p-4 bg-white"
            >
              <!-- Reviewer Info -->
              <div class="flex items-center mb-3">
                <img
                  v-if="review.reviewer?.profilePhotoUrl"
                  :src="review.reviewer.profilePhotoUrl"
                  :alt="review.reviewer?.displayName || 'Reviewer'"
                  class="w-10 h-10 rounded-full mr-3"
                />
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900">
                    {{ review.reviewer?.displayName || "Anonymous" }}
                  </h4>
                  <p class="text-sm text-gray-500">
                    {{ formatReviewDate(review.createTime) }}
                  </p>
                </div>
                <!-- Star Rating -->
                <div class="flex items-center">
                  <div class="flex">
                    <v-icon
                      v-for="n in 5"
                      :key="n"
                      small
                      :color="
                        n <= getStarRating(review.starRating)
                          ? 'amber'
                          : 'grey lighten-2'
                      "
                    >
                      mdi-star
                    </v-icon>
                  </div>
                </div>
              </div>

              <!-- Review Comment -->
              <div v-if="review.comment" class="mb-3">
                <p class="text-gray-800 leading-relaxed">
                  {{ review.comment }}
                </p>
              </div>

              <!-- Review Reply -->
              <div
                v-if="review.reviewReply?.comment"
                class="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r"
              >
                <div class="flex items-center mb-2">
                  <v-icon small color="blue">mdi-reply</v-icon>
                  <span class="ml-2 text-sm font-medium text-blue-800"
                    >Business Response</span
                  >
                  <span class="ml-auto text-xs text-blue-600">
                    {{ formatReviewDate(review.reviewReply.updateTime) }}
                  </span>
                </div>
                <p class="text-blue-800 text-sm">
                  {{ review.reviewReply.comment }}
                </p>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="sticky bottom-0 bg-white border-t">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeKeywordReviewsModal">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Staff Reviews Modal -->
    <v-dialog v-model="showStaffReviewsModal" max-width="800px" scrollable @click:outside="closeStaffReviewsModal">
      <v-card>
        <v-card-title
          class="text-md grey lighten-2 d-flex justify-space-between align-center sticky top-0 z-10"
        >
          <div class="flex-grow-1">
            Reviews mentioning "{{ selectedStaffForReviews?.name }}"
          </div>
          <div class="text-caption text-grey-darken-1">
            ({{ staffReviews.length }} reviews)
          </div>
        </v-card-title>

        <v-card-text class="pt-0">
          <!-- Add sorting controls -->
          <div
            class="mb-4 flex items-center gap-4 sticky top-0 bg-white z-10 py-4 border-b"
          >
            <v-select
              v-model="reviewsSortField"
              :items="[
                { text: 'Date', value: 'createTime' },
                { text: 'Rating', value: 'starRating' },
              ]"
              label="Sort by"
              dense
              outlined
              hide-details
              class="w-32"
            />
            <v-btn-toggle v-model="reviewsSortOrder" mandatory>
              <v-btn small value="asc">
                <v-icon>mdi-sort-ascending</v-icon>
              </v-btn>
              <v-btn small value="desc">
                <v-icon>mdi-sort-descending</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-select
              v-model="reviewsPerPage"
              :items="[5, 10, 20, 50, { text: 'All', value: -1 }]"
              label="Reviews per page"
              dense
              outlined
              hide-details
              class="w-32"
            />
          </div>

          <div v-if="isLoadingStaffReviews" class="text-center py-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <p class="mt-4 text-gray-600">Loading reviews...</p>
          </div>

          <div v-else-if="staffReviews.length === 0" class="text-center py-6">
            <p class="text-gray-500">
              No reviews found mentioning this staff member.
            </p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="(review, idx) in paginatedAndSortedStaffReviews"
              :key="review.reviewId || idx"
              class="border border-gray-200 rounded-lg p-4 bg-white"
            >
              <!-- Reviewer Info -->
              <div class="flex items-center mb-3">
                <img
                  v-if="review.reviewer?.profilePhotoUrl"
                  :src="review.reviewer.profilePhotoUrl"
                  :alt="review.reviewer?.displayName || 'Reviewer'"
                  class="w-10 h-10 rounded-full mr-3"
                />
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900">
                    {{ review.reviewer?.displayName || "Anonymous" }}
                  </h4>
                  <p class="text-sm text-gray-500">
                    {{ formatReviewDate(review.createTime) }}
                  </p>
                </div>
                <!-- Star Rating -->
                <div class="flex items-center">
                  <div class="flex">
                    <v-icon
                      v-for="n in 5"
                      :key="n"
                      small
                      :color="
                        n <= getStarRating(review.starRating)
                          ? 'amber'
                          : 'grey lighten-2'
                      "
                    >
                      mdi-star
                    </v-icon>
                  </div>
                </div>
              </div>

              <!-- Review Comment -->
              <div v-if="review.comment" class="mb-3">
                <p class="text-gray-800 leading-relaxed">
                  {{ review.comment }}
                </p>
              </div>

              <!-- Review Reply -->
              <div
                v-if="review.reviewReply?.comment"
                class="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r"
              >
                <div class="flex items-center mb-2">
                  <v-icon small color="blue">mdi-reply</v-icon>
                  <span class="ml-2 text-sm font-medium text-blue-800"
                    >Business Response</span
                  >
                  <span class="ml-auto text-xs text-blue-600">
                    {{ formatReviewDate(review.reviewReply.updateTime) }}
                  </span>
                </div>
                <p class="text-blue-800 text-sm">
                  {{ review.reviewReply.comment }}
                </p>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="sticky bottom-0 bg-white border-t">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeStaffReviewsModal">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Location-Based Insights -->
    <div
      v-if="hasData"
      class="bg-white rounded-lg shadow-sm mb-6 overflow-hidden"
    >
      <div
        class="bg-gray-50 px-6 py-3 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center"
      >
        <h2 class="text-lg font-medium text-gray-800">Location Insights</h2>
        <div class="flex gap-2 ml-auto items-center flex-wrap">
          <div class="text-sm text-gray-600 whitespace-nowrap">Sort by:</div>
          <div class="btn-group">
            <v-btn
              small
              :outlined="locationSortOption !== 'positive'"
              :color="locationSortOption === 'positive' ? 'success' : ''"
              @click="
                locationSortOption = 'positive';
                sortLocationInsights();
              "
              class="rounded-r-none"
            >
              Most Positive
            </v-btn>
            <v-btn
              small
              :outlined="locationSortOption !== 'sentiment_high'"
              :color="locationSortOption === 'sentiment_high' ? 'primary' : ''"
              @click="
                locationSortOption = 'sentiment_high';
                sortLocationInsights();
              "
              class="rounded-none"
            >
              Sentiment (Highest First)
            </v-btn>
            <v-btn
              small
              :outlined="locationSortOption !== 'sentiment_low'"
              :color="locationSortOption === 'sentiment_low' ? 'primary' : ''"
              @click="
                locationSortOption = 'sentiment_low';
                sortLocationInsights();
              "
              class="rounded-none"
            >
              Sentiment (Lowest First)
            </v-btn>
            <v-btn
              small
              :outlined="locationSortOption !== 'negative'"
              :color="locationSortOption === 'negative' ? 'error' : ''"
              @click="
                locationSortOption = 'negative';
                sortLocationInsights();
              "
              class="rounded-l-none"
            >
              Most Negative
            </v-btn>
          </div>
          <v-btn
            small
            text
            color="primary"
            @click="showAllLocationInsights = !showAllLocationInsights"
          >
            {{ showAllLocationInsights ? "Show Less" : "Show All" }}
          </v-btn>
        </div>
      </div>

      <div class="p-4">
        <v-expansion-panels v-if="locationInsights.length > 0">
          <v-expansion-panel
            v-for="(location, index) in showAllLocationInsights
              ? locationInsights
              : locationInsights.slice(0, 5)"
            :key="index"
          >
            <v-expansion-panel-header class="py-2">
              <div class="flex justify-between items-center w-full gap-x-4">
                <div class="w-[400px] min-w-0">
                  <div class="font-medium text-gray-700 truncate">
                    {{ location.name }}
                  </div>
                  <div
                    v-if="location.areaCity"
                    class="text-xs text-gray-500 mt-1 truncate"
                  >
                    {{ location.areaCity }}
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <v-chip small class="bg-green-50 text-green-700"
                    >{{ location.positiveCount }} positive</v-chip
                  >
                  <v-chip small class="bg-red-50 text-red-700"
                    >{{ location.negativeCount }} negative</v-chip
                  >
                  <v-chip
                    small
                    :color="
                      getLocationSentimentVuetifyColor(location.sentimentScore)
                    "
                    class="font-weight-bold ml-2"
                  >
                    Sentiment: {{ location.sentimentScore.toFixed(1) }}
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <h4 class="text-sm font-medium text-green-700 mb-2">
                    Positive Aspects
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    <v-chip
                      v-for="(keyword, idx) in location.positiveKeywords"
                      :key="idx"
                      small
                      label
                      class="bg-green-50 text-green-700"
                    >
                      {{ keyword.text }} ({{ keyword.count }})
                    </v-chip>
                    <div
                      v-if="location.positiveKeywords.length === 0"
                      class="text-sm text-gray-500"
                    >
                      No positive keywords found
                    </div>
                  </div>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-red-700 mb-2">
                    Areas for Improvement
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    <v-chip
                      v-for="(keyword, idx) in location.negativeKeywords"
                      :key="idx"
                      small
                      label
                      class="bg-red-50 text-red-700"
                    >
                      {{ keyword.text }} ({{ keyword.count }})
                    </v-chip>
                    <div
                      v-if="location.negativeKeywords.length === 0"
                      class="text-sm text-gray-500"
                    >
                      No negative keywords found
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <div v-else class="text-center py-8 text-gray-500">
          No location-specific insights available
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "@/utils/firebase.utils";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";
import LocationDropdown from "@/components/LocationDropdown.vue";
import ComparisonIndicator from "@/components/ComparisonIndicator.vue";
import { uniqueArray } from "@/utils/common.utils";

export default {
  components: {
    LocationDropdown,
    ComparisonIndicator,
  },
  name: "GmbReviewsReport",

  data() {
    return {
      showScreenshotHeader: false,
      workspaceData: null,
      selectedDate: null,
      dateOptions: [],
      selectedWIDs: [],
      isLoadingDates: false,
      isGenerating: false,
      reportData: null,
      stores: [],
      showStoreDropdown: false,
      selectedWID: null,
      reviewsCount: null,
      selectedStore: null,
      locationSearch: "",
      isDownloading: false,
      isSending: false,
      locationRatings: null,
      isLoadingRatings: false,
      selectedRatingRange: null,
      reportFetched: false,
      // Add new properties for reviews sorting and pagination
      reviewsSortField: "createTime",
      reviewsSortOrder: "desc",
      reviewsPerPage: 5,
      reviewsCurrentPage: 1,
      ratingRanges: [
        { text: "All Ratings", value: null },
        { text: "Above 4 Stars", value: "4" },
        { text: "3-4 Stars", value: "3" },
        { text: "2-3 Stars", value: "2" },
        { text: "Below 2 Stars", value: "0" },
      ],
      // Add time period selection with default to 12 months
      selectedTimePeriod: "THREE", // Default to 12 months
      timePeriodOptions: [
        { text: "Last 3 Months", value: "ONE" },
        { text: "Last 6 Months", value: "TWO" },
        { text: "Last 12 Months", value: "THREE" }, // Default selected
        { text: "All Time", value: "ALL" },
      ],
      storesToShow: 20,
      totalAverageRating: "0",
      lastGeneratedAt: null,
      // Modal properties
      showLocationModal: false,
      selectedLocations: [],
      selectedKeyword: null,
      keywordLocationSearch: "",
      currentPage: 1,
      itemsPerPage: 10,
      showAllKeywordsModal: false,
      allKeywords: [],
      selectedMonth: "all", // Changed default value to 'all'
      monthOptions: [{ text: "Past Months", value: "all" }], // Base options, will be populated with available months
      showAllFeedbackModal: false,
      allFeedback: [],
      feedbackType: null,

      // New properties for enhanced UI
      showAllLocations: false,
      showAllLocationInsights: true, // Default to showing all locations
      ratingTrendData: [],
      topPositiveKeywords: [],
      topNegativeKeywords: [],
      topStaffMentions: [],
      locationInsights: [],
      monthlyTableData: [],
      isTableLoading: false,

      // New properties for caching
      cacheExpiresAt: null,
      isBackgroundRefreshing: false,
      cacheExpirationTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
      cacheRefreshInterval: null,

      // New flag to prevent auto-refresh during initial component mount
      isInitialMount: true,
      locationSortOption: "positive",

      // Context menu properties
      showContextMenu: false,
      menuX: 0,
      menuY: 0,
      hasInitializedWithGlobalStores: false, // Flag to ensure watcher runs init logic once
      filteredReportData: null, // To store filtered data
      availableMonths: [], // To store available months for filter

      // Keyword Reviews Modal properties
      showKeywordReviewsModal: false,
      keywordReviews: [],
      isLoadingKeywordReviews: false,
      selectedKeywordForReviews: null,
      selectedLocationName: "",

      // Staff Mentions Modal properties
      showStaffReviewsModal: false,
      staffReviews: [],
      isLoadingStaffReviews: false,
      selectedStaffForReviews: null,

      // Staff Mentions Location Modal properties
      showAllStaffMentionsModal: false,
      allStaffMentions: [],
      selectedStaffLocationModal: false, // Similar to keyword location modal
      // Location-specific monthly data storage
      locationMonthlyDataMap: {},
    };
  },

  computed: {
    // Add new computed properties for reviews sorting and pagination
    paginatedAndSortedReviews() {
      if (!this.keywordReviews.length) return [];

      // First sort the reviews
      const sorted = [...this.keywordReviews].sort((a, b) => {
        const aValue = a[this.reviewsSortField];
        const bValue = b[this.reviewsSortField];

        if (this.reviewsSortField === "createTime") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return this.reviewsSortOrder === "desc"
            ? dateB - dateA
            : dateA - dateB;
        }

        if (this.reviewsSortField === "starRating") {
          // Convert star rating text to numbers for proper sorting
          const ratingA = this.getStarRating(a.starRating);
          const ratingB = this.getStarRating(b.starRating);
          return this.reviewsSortOrder === "desc"
            ? ratingB - ratingA
            : ratingA - ratingB;
        }

        return 0;
      });

      // Return all reviews if reviewsPerPage is -1
      if (this.reviewsPerPage === -1) {
        return sorted;
      }

      // Otherwise paginate
      const start = (this.reviewsCurrentPage - 1) * this.reviewsPerPage;
      const end = start + this.reviewsPerPage;
      return sorted.slice(start, end);
    },

    // Add computed property for paginated and sorted staff reviews
    paginatedAndSortedStaffReviews() {
      if (!this.staffReviews.length) return [];

      // First sort the staff reviews
      const sorted = [...this.staffReviews].sort((a, b) => {
        const aValue = a[this.reviewsSortField];
        const bValue = b[this.reviewsSortField];

        if (this.reviewsSortField === "createTime") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return this.reviewsSortOrder === "desc"
            ? dateB - dateA
            : dateA - dateB;
        }

        if (this.reviewsSortField === "starRating") {
          // Convert star rating text to numbers for proper sorting
          const ratingA = this.getStarRating(a.starRating);
          const ratingB = this.getStarRating(b.starRating);
          return this.reviewsSortOrder === "desc"
            ? ratingB - ratingA
            : ratingA - ratingB;
        }

        return 0;
      });

      // Return all reviews if reviewsPerPage is -1
      if (this.reviewsPerPage === -1) {
        return sorted;
      }

      // Otherwise paginate
      const start = (this.reviewsCurrentPage - 1) * this.reviewsPerPage;
      const end = start + this.reviewsPerPage;
      return sorted.slice(start, end);
    },

    totalReviewPages() {
      return Math.ceil(this.keywordReviews.length / this.reviewsPerPage);
    },
    currentWorkspace() {
      return this.$store.getters.currentWorkspace;
    },
    currentWorkspaceName() {
      return (
        this.workspaceData?.name || this.currentWorkspace?.name || "Workspace"
      );
    },
    currentWorkspaceLogo() {
      return (
        this.workspaceData?.logo_original ||
        this.currentWorkspace?.logo_original
      );
    },
    currentDate() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    },
    selectedCount() {
      return new Set(this.selectedWIDs).size;
    },
    filteredStores() {
      const searchTerm = this.locationSearch.toLowerCase();
      return this.stores.filter((store) => {
        const matchesSearch =
          store.name?.toLowerCase()?.includes(searchTerm) ||
          store?.sub_locality?.toLowerCase()?.includes(searchTerm) ||
          store?.address?.toLowerCase()?.includes(searchTerm) ||
          store?.city?.toLowerCase()?.includes(searchTerm);

        const matchesRating = this.selectedRatingRange
          ? this.selectedRatingRange === "0"
            ? store.averageRating < 2
            : store.averageRating >= Number(this.selectedRatingRange) &&
              store.averageRating < Number(this.selectedRatingRange) + 1
          : true;

        return matchesSearch && matchesRating;
      });
    },

    paginatedStores() {
      return this.filteredStores.slice(0, this.storesToShow);
    },

    hasData() {
      return (
        this.reportData?.data && Object.keys(this.reportData.data).length > 0
      );
    },

    currentMonthData() {
      if (!this.reportData?.data) return null;
      // Get the first month's data if no specific month is selected
      const monthKey = Object.keys(this.reportData.data)[0];
      return this.reportData.data[monthKey];
    },

    isLocationDropdownDisabled() {
      return this.selectedRatingRange !== null;
    },

    canGenerateReport() {
      return (
        !this.isLoadingRatings &&
        (!this.selectedRatingRange || this.locationRatings)
      );
    },

    filteredLocations() {
      if (!this.keywordLocationSearch) {
        return this.selectedLocations;
      }
      const searchTerm = this.keywordLocationSearch.toLowerCase();
      return this.selectedLocations.filter((location) => {
        const name = this.findLocationName(location.id)?.toLowerCase() || "";
        const address =
          this.findLocationAddress(location.id)?.toLowerCase() || "";
        return name.includes(searchTerm) || address.includes(searchTerm);
      });
    },

    paginatedLocations() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLocations.slice(start, end);
    },

    // Compute rating trend points for visualization
    ratingTrendPoints() {
      if (
        !this.reportData?.data ||
        Object.keys(this.reportData.data).length === 0 ||
        this.ratingTrendData.length === 0
      ) {
        return "";
      }

      // Generate SVG points
      const width = 300;
      const height = 100;
      const padding = 10;
      const availableWidth = width - padding * 2;
      const availableHeight = height - padding * 2;

      return this.ratingTrendData
        .map((rating, index) => {
          const x =
            index * (availableWidth / (this.ratingTrendData.length - 1)) +
            padding;
          const y = height - (rating / 5) * availableHeight - padding;
          return `${x},${y}`;
        })
        .join(" ");
    },

    // Filter report data based on selected month
    effectiveReportData() {
      if (!this.reportData?.data) return null;
      if (!this.selectedMonth || this.selectedMonth === "all")
        return this.reportData;

      // Create a filtered version of the report data
      return {
        ...this.reportData,
        data: {
          [this.selectedMonth]: this.reportData.data[this.selectedMonth],
        },
        keywordLocationMap:
          this.selectedMonth in this.reportData.keywordLocationMap
            ? {
                [this.selectedMonth]:
                  this.reportData.keywordLocationMap[this.selectedMonth],
              }
            : {},
        staffLocationMap:
          this.selectedMonth in (this.reportData.staffLocationMap || {})
            ? {
                [this.selectedMonth]:
                  this.reportData.staffLocationMap[this.selectedMonth],
              }
            : {},
      };
    },

    // Use this instead of reportData.data in all visualizations
    activeData() {
      return this.effectiveReportData?.data || {};
    },
  },

  methods: {
    async fetchWorkspaceData() {
      if (!this.WID || !this.currentUID) {
        console.warn("Cannot fetch workspace data: missing WID or currentUID");
        return;
      }

      try {
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        } else {
          console.warn("Workspace not found in Firestore for WID:", this.WID);
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      }
    },
    findLocationAddress(locationId) {
      if (!locationId) return "";

      const location = this.stores.find(
        (item) =>
          item.locationId === locationId ||
          item.placeId === locationId ||
          item.id === locationId ||
          item.wid === locationId
      );

      return location?.shallowConnect
        ? location.address
        : location?.sub_locality || "";
    },
    findLocationName(id) {
      // Parameter changed to generic 'id'
      if (!id) return "Unknown Location";

      // Reverted to general lookup to handle both internal IDs and GMB IDs
      const store = this.stores.find(
        (item) =>
          item.locationId === id ||
          item.placeId === id ||
          item.id === id ||
          item.wid === id
      );

      return store?.name || "Unknown Location";
    },
    showLocationsForKeyword(keyword, month) {
      // We'll need the month to access the correct keyword map
      const currentMonth = month || Object.keys(this.reportData.data)[0]; // Default to first month if not provided
      const key = `${keyword.text}-${keyword.type}`;

      // If no specific month provided, aggregate location data from all months
      if (!month && this.reportData?.keywordLocationMap) {
        const allMonthsLocations = [];
        const processedLocationIds = new Set();

        // Iterate through all months
        Object.keys(this.reportData.keywordLocationMap).forEach((monthKey) => {
          if (this.reportData.keywordLocationMap[monthKey][key]) {
            // For each location in this month
            this.reportData.keywordLocationMap[monthKey][key].forEach(
              (location) => {
                // If we haven't processed this location yet
                console.log("This is what we have in location ", location);
                if (!processedLocationIds.has(location.id)) {
                  allMonthsLocations.push({ ...location });
                  processedLocationIds.add(location.id);
                } else {
                  // Update count for existing location
                  const existingLocation = allMonthsLocations.find(
                    (loc) => loc.id === location.id
                  );
                  if (existingLocation) {
                    existingLocation.count += location.count;
                    existingLocation.reviews = [
                      ...new Set([
                        ...(existingLocation.reviews || []),
                        ...location.reviews,
                      ]),
                    ];
                  }
                }
              }
            );
          }
        });

        // Sort by count (highest first)
        allMonthsLocations.sort((a, b) => b.count - a.count);

        this.selectedLocations = allMonthsLocations;
        this.selectedKeyword = keyword;
        this.showLocationModal = true;
        return;
      }

      // Check that keywordLocationMap exists and has the correct keys
      if (
        this.reportData?.keywordLocationMap &&
        this.reportData.keywordLocationMap[currentMonth] &&
        this.reportData.keywordLocationMap[currentMonth][key]
      ) {
        this.selectedLocations =
          this.reportData.keywordLocationMap[currentMonth][key];
      } else {
        console.warn(
          `Location data not found for keyword: ${key} in month: ${currentMonth}`
        );
        this.selectedLocations = [];
      }

      this.selectedKeyword = keyword;
      this.showLocationModal = true;
    },
    async handleLocationChange(profile) {
      // Store previous selection for comparison
      const previousSelected = [...this.selectedWIDs];
      this.selectedWIDs = uniqueArray(
        this.stores.filter((item) => item.checked).map((item) => item.id)
      ); // Extract their IDs

      // If selectedWIDs is empty, push all item IDs
      if (this.selectedWIDs.length === 0) {
        this.selectedWIDs = this.stores.map((item) => item.id);
      }

      this.selectedWID = profile.wid;
      let query = { ...this.$route.query };
      if (this.selectedWIDs.length > 0) {
        this.modifyUrlParams("location", this.selectedWIDs.join(","));
      } else {
        delete query.location;
        this.$router.replace({ query });
      }
      // Check if selection actually changed
      const hasSelectionChanged =
        previousSelected.length !== this.selectedWIDs.length ||
        previousSelected.some((id) => !this.selectedWIDs.includes(id));

      // If selection changed, clear the existing report data to force a refresh
      if (hasSelectionChanged && !this.isGenerating) {
        // Clear cached data for this specific set of locations
        localStorage.removeItem("gmbReviewReportCache");
        // Generate a new report with the updated locations
        this.generateReport();
      }
    },
    modifyUrlParams(param, value) {
      // Check if the current value in the URL is already the same
      if (this.$route.query[param] === value) {
        return; // Skip redundant navigation
      }
      // Only update the URL if the value is different
      this.$router.replace({ query: { ...this.$route.query, [param]: value } });
    },
    async generateReport() {
      this.isGenerating = true;
      try {
        await this.generateReportInternal(false);

        // Add notification for successful report generation
        this.showCompletionNotification();
      } catch (error) {
        console.error("Error generating report:", error);
        this.$alert.show(error.message || "Failed to generate report");
      } finally {
        this.isGenerating = false;
        this.reportFetched = true;
      }
    },

    // New method to show completion notification
    showCompletionNotification() {
      // Check if the component is still mounted
      if (this.$el) {
        // Create a completion notification that auto-dismisses
        const notification = document.createElement("div");
        notification.className =
          "fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-md z-50 transition-opacity duration-500";
        notification.innerHTML = `
          <div class="flex items-center">
            <v-icon color="success" small class="mr-2">mdi-check-circle</v-icon>
            <span>Report generated successfully!</span>
          </div>
        `;

        document.body.appendChild(notification);

        // Fade out and remove after 3 seconds
        setTimeout(() => {
          notification.style.opacity = "0";
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 500);
        }, 3000);
      }
    },

    // Internal implementation of report generation (can be called for background refresh)
    async generateReportInternal(isBackground) {
      if (!isBackground) {
        this.isTableLoading = true;
        // Clear monthly table while loading
        this.monthlyTableData = [];
      }

      try {
        let payload = {
          action: "generate_report",
          uid: this.currentUID,
        };

        let storesForReport =
          this.selectedWIDs.length > 0
            ? this.stores.filter((store) =>
                this.selectedWIDs.includes(store.wid)
              )
            : this.stores;
        if (this.selectedRatingRange) {
          const minRating = Number(this.selectedRatingRange);
          const maxRating = minRating + 1;
          storesForReport = storesForReport.filter((store) => {
            const storeRating = store.averageRating || 0;
            return minRating === 0
              ? storeRating < 2
              : storeRating >= minRating && storeRating < maxRating;
          });
        }

        const locations = storesForReport
          .map((store) => ({
            locationId: store.locationId || store.placeId,
            accountId: store.gmbAccountId || null,
          }))
          .filter((loc) => loc.locationId);

        payload = {
          ...payload,
          locations,
          uid: this.currrentUID || auth.currentUser.uid,
          wid: this.WID,
          ratingRange: this.selectedRatingRange,
        };
        console.log("THis is the payload ", payload);
        const idToken = await auth.currentUser.getIdToken();
        // check how much time it tokk to get the response
        const startTime = Date.now();
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/gmbReviewsReport`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );
        let insights_data = null;
        if (response.ok) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Time taken: ${duration} milliseconds`);
          const resultt = await response.json();
          console.log("resultt::", resultt);
          insights_data = resultt.data;
          
          // Store location-specific monthly data for table generation
          this.locationMonthlyDataMap = {};
          
          // Aggregate data across locations
          const aggregatedData = {};
          const keywordLocationMap = {}; // We'll now organize by month-keyword
          const staffLocationMap = {}; // Similar structure for staff mentions

          Object.entries(resultt.data)?.forEach(
            ([locationId, locationData]) => {
              // Initialize storage for this location if not exists
              if (!this.locationMonthlyDataMap[locationId]) {
                this.locationMonthlyDataMap[locationId] = {};
              }
              
              Object.entries(locationData).forEach(([month, stats]) => {
                // Store location-specific monthly data
                this.locationMonthlyDataMap[locationId][month] = {
                  totalReviews: stats.totalReviews || 0,
                  ratingDistribution: stats.ratingDistribution || {},
                  repliedCount: stats.repliedCount || 0,
                  unrepliedCount: stats.unrepliedCount || 0,
                  textReviews: stats.textReviews || 0,
                  needsProcessing: stats.needsProcessing || false,
                  reviews: stats.reviews || [],
                  keywords: stats.keywords || [],
                  stf: stats.stf || []
                };
                
                if (!aggregatedData[month]) {
                  aggregatedData[month] = {
                    aggregated: {
                      totalReviews: 0,
                      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                      repliedCount: 0,
                      unrepliedCount: 0,
                      feedback: {
                        positive: [],
                        negative: [],
                        neutral: [],
                        keywords: [],
                      },
                      keywords: [],
                      stf: [],
                    },
                  };
                }

                // Make sure month exists in keywordLocationMap and staffLocationMap
                if (!keywordLocationMap[month]) {
                  keywordLocationMap[month] = {};
                }
                if (!staffLocationMap[month]) {
                  staffLocationMap[month] = {};
                }

                const monthAggregated = aggregatedData[month].aggregated;
                monthAggregated.totalReviews += stats.totalReviews || 0;
                Object.entries(stats.ratingDistribution || {}).forEach(
                  ([rating, count]) => {
                    monthAggregated.ratingDistribution[rating] =
                      (monthAggregated.ratingDistribution[rating] || 0) + count;
                  }
                );
                monthAggregated.repliedCount += stats.repliedCount || 0;
                monthAggregated.unrepliedCount += stats.unrepliedCount || 0;

                // Ensure stats.feedback exists before trying to access its properties
                const statsFeedback = stats.feedback || {};

                // Aggregate feedback (unique points) with defensive coding
                monthAggregated.feedback.positive = [
                  ...new Set([
                    ...monthAggregated.feedback.positive,
                    ...(statsFeedback.positive || []),
                  ]),
                ];
                monthAggregated.feedback.negative = [
                  ...new Set([
                    ...monthAggregated.feedback.negative,
                    ...(statsFeedback.negative || []),
                  ]),
                ];
                monthAggregated.feedback.neutral = [
                  ...new Set([
                    ...monthAggregated.feedback.neutral,
                    ...(statsFeedback.neutral || []),
                  ]),
                ];

                // Aggregate keywords and map to location IDs with counts
                (stats.keywords || []).forEach((kw) => {
                  const key = `${kw.text}-${kw.type}`; // Unique key for each keyword
                  const inputReviews = [
                    ...new Set(kw.reviewIds || kw.reviews || []),
                  ];

                  // Make sure keyword exists in this month's map
                  if (!keywordLocationMap[month][key]) {
                    keywordLocationMap[month][key] = [];
                  }

                  // Check if this location already exists in the map for this month and keyword
                  const existingLocation = keywordLocationMap[month][key].find(
                    (item) => item.id === locationId
                  );
                  if (existingLocation) {
                    existingLocation.count += kw.count; // Increment count if exists
                    existingLocation.reviews = [
                      ...new Set([
                        ...(existingLocation.reviews || []),
                        ...inputReviews,
                      ]),
                    ];
                  } else {
                    keywordLocationMap[month][key].push({
                      id: locationId,
                      count: kw.count,
                      reviews: inputReviews,
                    }); // Add new location with count
                  }

                  const existingKw = monthAggregated.keywords.find(
                    (k) => k.text === kw.text && k.type === kw.type
                  );
                  if (existingKw) {
                    existingKw.count += kw.count;
                    existingKw.reviews = [
                      ...new Set([
                        ...(existingKw.reviews || []),
                        ...inputReviews,
                      ]),
                    ];
                  } else {
                    monthAggregated.keywords.push({
                      text: kw.text,
                      type: kw.type,
                      count: kw.count,
                      reviews: inputReviews,
                    });
                  }
                });

                // Aggregate staff mentions and create location mapping
                (stats.stf || []).forEach((staff) => {
                  const staffName = staff.name;
                  const inputReviews = [
                    ...new Set(staff.reviewIds || []),
                  ];

                  // Create staff location mapping (similar to keyword mapping)
                  if (!staffLocationMap[month][staffName]) {
                    staffLocationMap[month][staffName] = [];
                  }

                  // Check if this location already exists for this staff member
                  const existingLocation = staffLocationMap[month][staffName].find(
                    (item) => item.id === locationId
                  );
                  if (existingLocation) {
                    existingLocation.count += staff.count;
                    existingLocation.reviews = [
                      ...new Set([
                        ...(existingLocation.reviews || []),
                        ...inputReviews,
                      ]),
                    ];
                  } else {
                    staffLocationMap[month][staffName].push({
                      id: locationId,
                      count: staff.count,
                      reviews: inputReviews,
                    });
                  }

                  // Aggregate staff mentions at month level (existing logic)
                  const existingStaff = monthAggregated.stf.find(
                    (s) => s.name === staff.name
                  );
                  if (existingStaff) {
                    existingStaff.count += staff.count;
                    existingStaff.reviewIds = [
                      ...new Set([
                        ...(existingStaff.reviewIds || []),
                        ...inputReviews,
                      ]),
                    ];
                  } else {
                    monthAggregated.stf.push({
                      name: staff.name,
                      count: staff.count,
                      reviewIds: [...inputReviews],
                    });
                  }
                });
              });
            }
          );

          // Sort staff mentions by count in descending order for each month
          Object.values(aggregatedData).forEach((monthData) => {
            if (monthData.aggregated?.stf && monthData.aggregated.stf.length > 0) {
              monthData.aggregated.stf.sort((a, b) => {
                // First sort by count in descending order
                const countDiff = b.count - a.count;
                if (countDiff !== 0) return countDiff;
                // If counts are equal, sort alphabetically by name
                return a.name.localeCompare(b.name);
              });
            }
          });

          // Sort location lists by count in descending order for each month and keyword
          Object.keys(keywordLocationMap)?.forEach((month) => {
            Object.keys(keywordLocationMap[month]).forEach((key) => {
              keywordLocationMap[month][key].sort((a, b) => b.count - a.count);
            });
          });

          // Sort location lists by count for staff mentions as well
          Object.keys(staffLocationMap)?.forEach((month) => {
            Object.keys(staffLocationMap[month]).forEach((staffName) => {
              staffLocationMap[month][staffName].sort((a, b) => b.count - a.count);
            });
          });

          this.reportData = { data: aggregatedData, keywordLocationMap, staffLocationMap };
          console.log("Report data generated with staffLocationMap:", !!staffLocationMap);
          console.log("Staff members found:", Object.keys(staffLocationMap).reduce((acc, month) => {
            acc[month] = Object.keys(staffLocationMap[month]);
            return acc;
          }, {}));

          // Save to cache
          this.saveToCache();

          // After successful report generation, update the trend data and keywords
          this.updateRatingTrendData();
          this.generateTopKeywords();
          this.generateTopStaffMentions();
          this.generateLocationInsights();

          // Only update table data if not in background mode
          if (!isBackground) {
            this.updateMonthlyTableData();
          }
        } else {
          console.error("Request failed with status:", response.status);
        }
      } catch (error) {
        console.error("Error generating report:", error);
        if (!isBackground) {
          this.$alert.show(error.message || "Failed to generate report");
        }
        throw error;
      } finally {
        if (!isBackground) {
          this.isTableLoading = false;
        }
      }
    },

    // Process and update monthly table data based on the selected time period
    updateMonthlyTableData() {
      this.isTableLoading = true;

      try {
        if (!this.activeData) {
          console.warn("No report data available for monthly table");
          this.monthlyTableData = [];
          this.isTableLoading = false;
          return;
        }

        // Sort months chronologically (oldest to newest)
        const sortedMonths = Object.keys(this.activeData).sort((a, b) => {
          return new Date(a) - new Date(b);
        });

        if (sortedMonths.length === 0) {
          console.warn("No months available in report data");
          this.monthlyTableData = [];
          this.isTableLoading = false;
          return;
        }

        // Filter based on selected time period
        let filteredMonths = [...sortedMonths];

        if (this.selectedTimePeriod === "ONE") {
          // Last 3 months
          filteredMonths = sortedMonths.slice(-3);
        } else if (this.selectedTimePeriod === "TWO") {
          // Last 6 months
          filteredMonths = sortedMonths.slice(-6);
        } else if (this.selectedTimePeriod === "THREE") {
          // Last 12 months
          filteredMonths = sortedMonths.slice(-12);
        }

        if (filteredMonths.length === 0) {
          console.warn("No months available after filtering");
          this.monthlyTableData = [];
          this.isTableLoading = false;
          return;
        }

        // Process each month's data
        const tableData = filteredMonths
          .map((monthKey) => {
            try {
              const monthData = this.activeData[monthKey].aggregated;
              if (!monthData) {
                console.warn(`No aggregated data for month ${monthKey}`);
                return null;
              }

              const ratingDistribution = monthData.ratingDistribution || {};

              // Calculate total reviews with ratings
              const totalRatingsCount = Object.values(
                ratingDistribution
              ).reduce((sum, count) => sum + count, 0);

              // Calculate average rating
              let totalStars = 0;
              Object.entries(ratingDistribution).forEach(([rating, count]) => {
                totalStars += Number(rating) * count;
              });
              const avgRating =
                totalRatingsCount > 0 ? totalStars / totalRatingsCount : 0;

              // Map numeric rating distribution to named keys
              const ratingMap = {
                1: "ONE",
                2: "TWO",
                3: "THREE",
                4: "FOUR",
                5: "FIVE",
              };

              const ratings = {};
              Object.entries(ratingDistribution).forEach(([rating, count]) => {
                ratings[ratingMap[rating]] = count;
              });

              // Get text reviews count (from comment count if available)
              const textReviews = monthData.commentCount || 0;

              return {
                monthKey,
                totalReviews: monthData.totalReviews || 0,
                averageRating: avgRating,
                ratings,
                textReviews,
                repliedReviews: monthData.repliedCount || 0,
              };
            } catch (err) {
              console.error(`Error processing month ${monthKey}:`, err);
              return null;
            }
          })
          .filter((item) => item !== null); // Remove any null entries

        // Sort the data by month (newest first) for display
        tableData.sort((a, b) => new Date(b.monthKey) - new Date(a.monthKey));

        this.monthlyTableData = tableData;
      } catch (err) {
        console.error("Error generating monthly table data:", err);
        this.monthlyTableData = [];
      } finally {
        // Use setTimeout to ensure the UI updates
        setTimeout(() => {
          this.isTableLoading = false;
        }, 300);
      }
    },

    // Force reload of data and clear cache
    forceReloadData() {
      // Clear cached data
      localStorage.removeItem("gmbReviewReportCache");

      // Reset data visualization states if needed
      if (!this.hasData) {
        // Show a loading indicator message
        const loadingMsg = document.createElement("div");
        loadingMsg.className =
          "fixed top-4 right-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded shadow-md z-50 transition-opacity duration-500";
        loadingMsg.innerHTML = `
          <div class="flex items-center">
            <v-icon color="info" small class="mr-2">mdi-information</v-icon>
            <span>Generating report, please wait...</span>
          </div>
        `;
        document.body.appendChild(loadingMsg);

        // Remove after 3 seconds
        setTimeout(() => {
          if (document.body.contains(loadingMsg)) {
            document.body.removeChild(loadingMsg);
          }
        }, 3000);
      }

      // Generate new report with a small delay to allow Vue to update the UI
      setTimeout(() => {
        this.generateReport();
      }, 100);
    },

    // Clear cache and reset state
    clearCache() {
      // Close the context menu
      this.showContextMenu = false;

      // Clear the cache
      localStorage.removeItem("gmbReviewReportCache");

      // Reset data
      this.reportData = null;
      this.lastGeneratedAt = null;
      this.cacheExpiresAt = null;
      this.ratingTrendData = [];
      this.topPositiveKeywords = [];
      this.topNegativeKeywords = [];
      this.topStaffMentions = [];
      this.locationInsights = [];
      this.monthlyTableData = [];

      // Show confirmation to user
      const notification = document.createElement("div");
      notification.className =
        "fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-md z-50 transition-opacity duration-500";
      notification.innerHTML = `
        <div class="flex items-center">
          <v-icon color="success" small class="mr-2">mdi-check-circle</v-icon>
          <span>Cache cleared successfully. Click Refresh Data to regenerate the report.</span>
        </div>
      `;

      document.body.appendChild(notification);

      // Fade out and remove after 5 seconds
      setTimeout(() => {
        notification.style.opacity = "0";
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 500);
      }, 5000);
    },

    formatTimeAgo(timestamp) {
      const now = new Date();
      const past = new Date(timestamp);
      const diffInSeconds = Math.floor((now - past) / 1000);

      const intervals = [
        { label: "year", seconds: 31536000 },
        { label: "month", seconds: 2592000 },
        { label: "day", seconds: 86400 },
        { label: "hour", seconds: 3600 },
        { label: "minute", seconds: 60 },
        { label: "second", seconds: 1 },
      ];

      for (const interval of intervals) {
        const count = Math.floor(diffInSeconds / interval.seconds);
        if (count > 0) {
          return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`;
        }
      }
      return "just now";
    },

    async fetchStores() {
      try {
        if (!this.currentUID || !this.WID) {
          console.log("Waiting for user data...");
          return;
        }

        this.selectedWIDs = [];
        const locationParam = this.$route.query.location;
        const locationIds = locationParam
          ? Array.isArray(locationParam)
            ? locationParam
            : [locationParam]
          : [];
        // Convert to array if it's a comma-separated string (e.g., "id1,id2,id3")
        const locationIdsArray =
          locationIds.length === 1 && locationIds[0].includes(",")
            ? locationIds[0].split(",").map((id) => id.trim())
            : locationIds;
        // console.log(this.globalStores.length, 'locationIdsArray:', locationIdsArray)
        // Process the existing store data
        this.globalStores.forEach((storeData) => {
          // Add computed properties that aren't stored in Firestore
          const shouldBeChecked =
            locationIdsArray?.length === 0 ||
            locationIdsArray.includes(storeData.id);
          this.stores.push({
            ...storeData,
            id: storeData.id,
            wid: storeData.id,
            type: "store",
            averageRating: storeData.averageRating || 0,
            checked: shouldBeChecked,
            isReview: true,
            locationId: storeData.gmbLocationId || storeData.placeId,
            reviewCount: storeData.reviewCount || 0, // Fetch reviewCount
            isSelectable: true,
            tooltipMessage: "",
          });
          if (shouldBeChecked) {
            // push only if it's not already in the array
            this.selectedWIDs.push(storeData.id);
            this.selectedWIDs = uniqueArray(this.selectedWIDs);
          }
        });
        this.$nextTick(() => {
          if (this.$refs.locationDropdown) {
            // Force the dropdown component to update its internal state based on the current selectedWIDs
            this.$refs.locationDropdown.$forceUpdate();
          }
        });
        this.storesToShow = this.stores.slice(0, this.storesToShow); // Initialize visible stores
        // Calculate total average rating correctly
        const totalRatings = this.stores.reduce((sum, store) => {
          const storeRating = store.averageRating || 0; // Ensure averageRating is fetched correctly
          const storeReviewCount = store.reviewCount || 0; // Use reviewCount or default to 0
          return sum + storeRating * storeReviewCount; // Calculate weighted rating
        }, 0);

        const totalCount = this.stores.reduce(
          (sum, store) => sum + (store.reviewCount || 0),
          0
        ); // Sum of all review counts
        this.reviewsCount = totalCount;
        this.totalAverageRating =
          totalCount > 0 ? (totalRatings / totalCount).toFixed(1) : 0;
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$alert?.show("Failed to fetch stores: " + error.message);
      }
    },

    calculateAverageRating(ratingDistribution) {
      if (!ratingDistribution) return 0;

      const totalRatings = Object.entries(ratingDistribution).reduce(
        (sum, [rating, count]) => sum + Number(rating) * count,
        0
      );
      const totalCount = Object.values(ratingDistribution).reduce(
        (sum, count) => sum + count,
        0
      );

      return totalCount > 0 ? totalRatings / totalCount : 0;
    },

    calculateResponseRate(monthData) {
      if (!monthData?.totalReviews) return 0;
      return ((monthData.repliedCount / monthData.totalReviews) * 100).toFixed(
        0
      );
    },

    getSentimentColor(type) {
      const colors = {
        positive: "bg-green-50 text-green-700",
        negative: "bg-red-50 text-red-700",
        neutral: "bg-blue-50 text-blue-700",
      };
      return colors[type] || "bg-gray-50 text-gray-700";
    },

    toggleStoreDropdown(event) {
      // if (this.isLocationDropdownDisabled) {
      //     return;
      // }
      event.stopPropagation();
      this.showStoreDropdown = !this.showStoreDropdown;
      if (this.showStoreDropdown) {
        this.locationSearch = "";
      }
    },

    handleClickOutside(event) {
      const dropdown = this.$refs.locationDropdown?.$refs.showStoreDropdown;
      const input = event.target.closest(".location-dropdown");
      if (!input && dropdown && !dropdown.contains(event.target)) {
        this.showStoreDropdown = false;
      }
    },

    selectStore(profile) {
      // console.log('Selecting store:', profile);
      this.selectedStore = profile;
      this.selectedWID = profile.wid;
      this.locationSearch = "";
      this.showStoreDropdown = false;

      // Generate report automatically when store is selected
    },

    generateDateOptions() {
      const options = [];
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      // Generate options for the past 12 months
      for (let i = 0; i < 12; i++) {
        const date = new Date(currentYear, currentMonth - i, 1);
        const year = date.getFullYear();
        const monthName = date.toLocaleString("default", { month: "long" });

        options.push({
          text: `${monthName} ${year}`,
          value: {
            year: year,
            month: date.getMonth() + 1,
          },
        });
      }

      this.dateOptions = options;
      this.selectedDate = null;
    },

    formatMonth(monthStr) {
      const [year, month] = monthStr.split("-");
      return new Date(year, month - 1).toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    },

    formatMonthShort(monthStr) {
      const [year, month] = monthStr.split("-");
      const date = new Date(year, month - 1);
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    },

    generateLocationMonthlyData(locationId) {
      console.log("Received location ID:", locationId);
      
      // Try to find data with different possible ID formats
      let locationData = null;
      
      // First try direct match
      if (this.locationMonthlyDataMap && this.locationMonthlyDataMap[locationId]) {
        locationData = this.locationMonthlyDataMap[locationId];
      } else {
        // Try to find a matching store and use its locationId or placeId
        const store = this.stores?.find(s => 
          s.id === locationId || 
          s.wid === locationId ||
          s.locationId === locationId ||
          s.placeId === locationId
        );
        
        if (store) {
          // Try with store's locationId
          if (store.locationId && this.locationMonthlyDataMap[store.locationId]) {
            locationData = this.locationMonthlyDataMap[store.locationId];
            console.log('Found data using store.locationId:', store.locationId);
          }
          // Try with store's placeId
          else if (store.placeId && this.locationMonthlyDataMap[store.placeId]) {
            locationData = this.locationMonthlyDataMap[store.placeId];
            console.log('Found data using store.placeId:', store.placeId);
          }
        }
      }
      
      if (!locationData) {
        console.log('No data found for location:', locationId);
        return [];
      }
      
      const monthlyData = [];
      console.log('Found location data:', locationData);
      
      // Iterate through all months for this location
      Object.entries(locationData).forEach(([monthKey, stats]) => {
        
        // Calculate average rating for this location in this month
        const ratingDist = stats.ratingDistribution || {};
        const totalRatings = Object.entries(ratingDist).reduce(
          (sum, [rating, count]) => sum + Number(rating) * count,
          0
        );
        const totalCount = Object.values(ratingDist).reduce(
          (sum, count) => sum + count,
          0
        );
        const avgRating = totalCount > 0 ? totalRatings / totalCount : 0;
        
        monthlyData.push({
          monthKey: monthKey,
          totalReviews: stats.totalReviews || 0,
          averageRating: avgRating,
          ratings: {
            FIVE: ratingDist[5] || 0,
            FOUR: ratingDist[4] || 0,
            THREE: ratingDist[3] || 0,
            TWO: ratingDist[2] || 0,
            ONE: ratingDist[1] || 0,
          },
          textReviews: stats.textReviews || 0,
          repliedReviews: stats.repliedCount || 0,
        });
      });
      
      console.log('Generated monthly data:', monthlyData);
      
      // Sort by month (most recent first)
      monthlyData.sort((a, b) => {
        const dateA = new Date(a.monthKey + '-01');
        const dateB = new Date(b.monthKey + '-01');
        return dateB - dateA;
      });
      console.log("Got this data ", monthlyData.slice(0,6));
      // Return only the last 6 months
      return monthlyData.slice(0, 6);
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },

    async downloadReport() {
      console.log('generateReportHTML - Data Check:');

      this.isDownloading = true;
      try {
        // Show header for PDF generation
        this.showScreenshotHeader = true;
      
       // Wait for DOM update
        await this.$nextTick();
      // Ensure we have data
      if (!this.reportData || !this.reportData.data) {
        console.warn('No reportData available in generateReportHTML');
      }
      
      const html2pdf = (await import("html2pdf.js")).default;
      const container = document.createElement("div");
        container.style.padding = "24px";
        container.style.fontFamily = "Arial, sans-serif";
        container.style.maxWidth = "1200px";
        container.style.margin = "0 auto";

        // Add the header section to the PDF
        const headerSection = document.createElement("div");
        headerSection.style.marginBottom = "32px";
        headerSection.style.padding = "24px";
        // Simplified background for better PDF rendering
        headerSection.style.backgroundColor = "#667eea";
        
        headerSection.style.borderRadius = "8px";
        headerSection.style.color = "white";
        headerSection.style.position = "relative";

        headerSection.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; position: relative; z-index: 1;">
            <div style="display: flex; align-items: center; gap: 12px;">
              ${
                this.currentWorkspaceLogo
                  ? `<img src="${this.currentWorkspaceLogo}" alt="Workspace Logo" style="width: 40px; height: 40px; border-radius: 8px; object-fit: cover; border: 2px solid rgba(255, 255, 255, 0.2);" />`
                  : ""
              }
              <span style="font-size: 24px; font-weight: 600; text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3); position: relative; top: -10px;>${
                this.currentWorkspaceName || "Workspace"
              }</span>
            </div>
            <div style="font-size: 16px; font-weight: 500; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); position: relative; top: -10px;>${
              this.currentWorkspaceName || "Workspace"
            }</div>
          </div>
        `;
        container.appendChild(headerSection);

        const title = this.selectedStore
          ? `GMB Reviews Insights - ${
              this.selectedStore.sub_locality || this.selectedStore.address
            }`
          : "GMB Reviews Insights - All Locations";
        const titleSection = document.createElement("div");
        titleSection.style.marginBottom = "24px";
        titleSection.innerHTML = `
          <h1 style="font-size: 24px; font-weight: 600; color: #1F2937; margin-bottom: 24px;">${title}</h1>
        `;
        container.appendChild(titleSection);

        // Add brand perspective to the report
        if (this.reportData?.data) {
          const brandSection = document.createElement("div");
          brandSection.style.marginBottom = "32px";
          brandSection.style.padding = "16px";
          brandSection.style.backgroundColor = "white";
          brandSection.style.borderRadius = "8px";
          brandSection.style.border = "1px solid #E5E7EB";
          brandSection.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";

          const sentiment = this.calculateBrandSentiment();
          const sentimentColor = this.getSentimentColor(sentiment);

          brandSection.innerHTML = `
            <h2 style="font-size: 18px; font-weight: 600; color: #1F2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB;">Brand Perception</h2>
            <div style="display: flex; flex-wrap: wrap; gap: 20px;">
              <!-- Sentiment Score -->
              <div style="width: 150px; text-align: center;">
                <div style="position: relative; width: 120px; height: 120px; margin: 0 auto;">
                  <svg viewBox="0 0 100 100" width="120" height="120">
                    <!-- Background Circle -->
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#E5E7EB" stroke-width="10" />
                    
                    <!-- Sentiment Arc -->
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="45" 
                      fill="none" 
                      stroke="${sentimentColor}" 
                      stroke-width="10"
                      stroke-dasharray="${this.calculateSentimentArc()} ${
            283 - this.calculateSentimentArc()
          }"
                      stroke-dashoffset="70"
                      stroke-linecap="round"
                    />
                    
                    <!-- Score Text -->
                    <text x="50" y="45" text-anchor="middle" font-size="18" font-weight="bold" fill="#374151">
                      ${sentiment.toFixed(1)}
                    </text>
                    <text x="50" y="65" text-anchor="middle" font-size="10" fill="#6B7280">
                      Sentiment Score
                    </text>
                  </svg>
                </div>
              </div>
              
              <!-- Brand Summary -->
              <div style="flex: 1; min-width: 300px;">
                <h3 style="font-size: 14px; font-weight: 500; color: #4B5563; margin-bottom: 12px;">Overall Summary</h3>
                <p style="font-size: 14px; color: #374151; margin-bottom: 16px; line-height: 1.5;">
                  ${this.generateBrandSummary()}
                </p>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
                  <div>
                    <h4 style="font-size: 14px; font-weight: 500; color: #047857; margin-bottom: 8px;">Top Strengths</h4>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                      ${this.topPositiveKeywords
                        .slice(0, 3)
                        .map(
                          (kw) => `
                        <span style="display: inline-block; padding: 4px 8px; color: #065F46; border-radius: 9999px; font-size: 12px;">
                          ${kw.text} (${kw.count})
                        </span>
                      `
                        )
                        .join("")}
                    </div>
                  </div>
                  <div>
                    <h4 style="font-size: 14px; font-weight: 500; color: #B91C1C; margin-bottom: 8px;">Top Concerns</h4>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                      ${this.topNegativeKeywords
                        .slice(0, 3)
                        .map(
                          (kw) => `
                        <span style="display: inline-block; padding: 4px 8px; color: #991B1B; border-radius: 9999px; font-size: 12px;">
                          ${kw.text} (${kw.count})
                        </span>
                      `
                        )
                        .join("")}
                    </div>
                  </div>
                </div>
                
                ${this.topStaffMentions.length > 0 ? `
                <div style="margin-top: 16px;">
                  <h4 style="font-size: 14px; font-weight: 500; color: #1D4ED8; margin-bottom: 8px;">Top Staff Mentions</h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                    ${this.topStaffMentions
                      .slice(0, 5)
                      .map(
                        (staff) => `
                      <span style="display: inline-block; padding: 4px 8px; color: #1E40AF; border-radius: 9999px; font-size: 12px;">
                        ${staff.name} (${staff.count})
                      </span>
                    `
                      )
                      .join("")}
                  </div>
                </div>
                ` : ''}
              </div>
            </div>
          `;
          container.appendChild(brandSection);
        }

        // Add location insights to the report
        if (this.locationInsights && this.locationInsights.length > 0) {
          const locationSection = document.createElement("div");
          locationSection.style.marginBottom = "32px";
          locationSection.style.padding = "16px";
          locationSection.style.backgroundColor = "white";
          locationSection.style.borderRadius = "8px";
          locationSection.style.border = "1px solid #E5E7EB";
          locationSection.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";

          // Location insights header
          locationSection.innerHTML = `
            <h2 style="font-size: 18px; font-weight: 600; color: #1F2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB;">Location Insights</h2>
          `;

          // Create location cards (show all locations in the PDF)
          const locationsContent = document.createElement("div");
          locationsContent.style.display = "flex";
          locationsContent.style.flexDirection = "column";
          locationsContent.style.gap = "20px";

          // Show all locations in the PDF instead of limiting to 10
          this.locationInsights.forEach((location) => {
            const locationCard = document.createElement("div");
            locationCard.style.border = "1px solid #E5E7EB";
            locationCard.style.borderRadius = "8px";
            locationCard.style.padding = "16px";
            locationCard.style.backgroundColor = "#FFFFFF";
            locationCard.style.pageBreakInside = "avoid";
            locationCard.style.width = "100%";

            locationCard.innerHTML = `
              <div style="display: flex; justify-content: space-between; margin-bottom: 12px; border-bottom: 1px solid #F3F4F6; padding-bottom: 8px;">
                <h3 style="font-size: 14px; font-weight: 600; color: #1F2937; margin: 0;">${
                  location.name
                }</h3>
                <div style="display: flex; gap: 8px;">
                  <span style="font-size: 12px; padding: 2px 8px; color: #065F46; border-radius: 12px;">
                    ${location.positiveCount} positive
                  </span>
                  <span style="font-size: 12px; padding: 2px 8px; color: #991B1B; border-radius: 12px;">
                    ${location.negativeCount} negative
                  </span>
                  <span style="font-size: 12px; padding: 2px 8px; background-color: #EEF2FF; color: #4F46E5; border-radius: 12px; font-weight: 500;">
                    Sentiment: ${location.sentimentScore ? location.sentimentScore.toFixed(1) : 'N/A'}
                  </span>
                </div>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <h4 style="font-size: 12px; font-weight: 500; color: #047857; margin-top: 0; margin-bottom: 8px;">
                    Positive Aspects
                  </h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                    ${location.positiveKeywords
                      .slice(0, 5)
                      .map(
                        (kw) => `
                      <span style="display: inline-block; padding: 2px 6px; color: #065F46; border-radius: 9999px; font-size: 10px; white-space: normal; overflow-wrap: break-word; word-wrap: break-word;">
                        ${kw.text} (${kw.count})
                      </span>
                    `
                      )
                      .join("")}
                    ${
                      location.positiveKeywords.length === 0
                        ? '<span style="font-size: 11px; color: #6B7280;">No positive keywords found</span>'
                        : ""
                    }
                  </div>
                </div>
                <div>
                  <h4 style="font-size: 12px; font-weight: 500; color: #B91C1C; margin-top: 0; margin-bottom: 8px;">
                    Areas for Improvement
                  </h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                    ${location.negativeKeywords
                      .slice(0, 5)
                      .map(
                        (kw) => `
                      <span style="display: inline-block; padding: 2px 6px; color: #991B1B; border-radius: 9999px; font-size: 10px; white-space: normal; overflow-wrap: break-word; word-wrap: break-word;">
                        ${kw.text} (${kw.count})
                      </span>
                    `
                      )
                      .join("")}
                    ${
                      location.negativeKeywords.length === 0
                        ? '<span style="font-size: 11px; color: #6B7280;">No negative keywords found</span>'
                        : ""
                    }
                  </div>
                </div>
              </div>
            `;

            locationsContent.appendChild(locationCard);
            
            // Add Monthly Reviews Table for this location
            // console.log('Generating monthly data for location:', location.id, location.name);
            // console.log('Available location IDs in map:', Object.keys(this.locationMonthlyDataMap));
            const locationMonthlyData = this.generateLocationMonthlyData(location.id);
            // console.log('Location monthly data result:', locationMonthlyData);
            if (locationMonthlyData && locationMonthlyData.length > 0) {
              const monthlyTableCard = document.createElement("div");
              monthlyTableCard.style.border = "1px solid #E5E7EB";
              monthlyTableCard.style.borderRadius = "8px";
              monthlyTableCard.style.padding = "16px";
              monthlyTableCard.style.backgroundColor = "#FFFFFF";
              monthlyTableCard.style.marginBottom = "16px";
              monthlyTableCard.style.width = "100%";
              
              monthlyTableCard.innerHTML = `
                <h3 style="font-size: 14px; font-weight: 600; color: #1F2937; margin-bottom: 12px;">Monthly Reviews & Rating Trends - ${location.name}</h3>
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                  <thead>
                    <tr style="background-color: #F9FAFB;">
                      <th style="padding: 8px; text-align: left; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">MONTH</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">REVIEWS</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">AVG. RATING</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">5★</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">4★</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">3★</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">2★</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">1★</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">WITH TEXT</th>
                      <th style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; font-weight: 600; color: #6B7280; text-transform: uppercase; font-size: 10px;">REPLIED</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${locationMonthlyData.map(month => {
                      const stars = [];
                      const avgRating = month.averageRating;
                      for (let i = 1; i <= 5; i++) {
                        if (i <= Math.floor(avgRating)) {
                          stars.push('<span style="color: #F59E0B;">★</span>');
                        } else if (i - 0.5 <= avgRating) {
                          stars.push('<span style="color: #F59E0B;">★</span>');
                        } else {
                          stars.push('<span style="color: #D1D5DB;">★</span>');
                        }
                      }
                      
                      return `
                      <tr style="border-bottom: 1px solid #E5E7EB;">
                        <td style="padding: 8px; border: 1px solid #E5E7EB; font-weight: 500; color: #374151;">${this.formatMonth(month.monthKey)}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.totalReviews}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #374151;">
                          <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
                            <span style="font-weight: 600;">${month.averageRating.toFixed(1)}</span>
                            ${stars.join('')}
                          </div>
                        </td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.ratings.FIVE || 0}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.ratings.FOUR || 0}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.ratings.THREE || 0}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.ratings.TWO || 0}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">${month.ratings.ONE || 0}</td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">
                          ${month.textReviews} (${month.totalReviews > 0 ? Math.round((month.textReviews / month.totalReviews) * 100) : 0}%)
                        </td>
                        <td style="padding: 8px; text-align: center; border: 1px solid #E5E7EB; color: #6B7280;">
                          ${month.repliedReviews} (${month.totalReviews > 0 ? Math.round((month.repliedReviews / month.totalReviews) * 100) : 0}%)
                        </td>
                      </tr>
                    `}).join('')}
                  </tbody>
                </table>
              `;
              
              locationsContent.appendChild(monthlyTableCard);
            }
          });

          locationSection.appendChild(locationsContent);

          container.appendChild(locationSection);
        }

        // Add selected locations to the report if applicable
        if (
          this.selectedWIDs.length > 0 &&
          this.selectedWIDs.length < this.stores.length
        ) {
          const locationsSection = document.createElement("div");
          locationsSection.style.marginBottom = "24px";
          locationsSection.style.padding = "16px";
          locationsSection.style.backgroundColor = "#F9FAFB";
          locationsSection.style.borderRadius = "8px";
          locationsSection.style.border = "1px solid #E5E7EB";

          const locationsCount = this.selectedWIDs.length;
          locationsSection.innerHTML = `
            <h2 style="font-size: 16px; font-weight: 500; color: #374151; margin-bottom: 12px;">Selected Locations (${locationsCount})</h2>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              ${this.selectedWIDs
                .map(
                  (locationId) => `
                <span style="display: inline-block; padding: 4px 12px 8px 12px; background-color: #EFF6FF; color: #3B82F6; border-radius: 16px; font-size: 12px; border: 1px solid #BFDBFE;">${this.findLocationName(
                  locationId
                )}</span>
              `
                )
                .join("")}
            </div>
          `;
          container.appendChild(locationsSection);
        }

        // Create summary section
        if (this.reportData?.data) {
          const summarySection = document.createElement("div");
          summarySection.style.marginBottom = "32px";
          summarySection.style.padding = "16px";
          summarySection.style.backgroundColor = "white";
          summarySection.style.borderRadius = "8px";
          summarySection.style.border = "1px solid #E5E7EB";
          summarySection.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";

          // Summary header
          summarySection.innerHTML = `
            <h2 style="font-size: 18px; font-weight: 600; color: #1F2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB;">Insights Summary</h2>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
              <!-- Overall Rating Summary -->
              <div style="padding: 16px; border: 1px solid #E5E7EB; border-radius: 8px; background-color: #FFFFFF;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
                  <h3 style="font-size: 14px; font-weight: 500; color: #4B5563; margin: 0;">Overall Rating</h3>
                  <span style="color: #F59E0B; font-size: 18px; font-weight: 700;">${this.calculateOverallRating().toFixed(
                    1
                  )} ★</span>
                </div>
                <p style="font-size: 12px; color: #6B7280; margin-bottom: 8px;">Based on ${this.calculateTotalReviews()} total reviews</p>
                <!-- Rating Distribution -->
                <div style="margin-top: 12px;">
                  ${[5, 4, 3, 2, 1]
                    .map(
                      (star) => `
                    <div style="display: flex; align-items: center; margin-bottom: 6px;">
                      <div style="width: 30px; text-align: right; margin-right: 8px; font-size: 12px;">${star}★</div>
                      <div style="flex-grow: 1; height: 8px; background-color: #E5E7EB; border-radius: 4px; overflow: hidden; margin-right: 8px;">
                        <div style="height: 100%; background-color: #F59E0B; width: ${this.calculateRatingPercentage(
                          star
                        )}%;"></div>
                      </div>
                      <div style="width: 40px; font-size: 12px; color: #6B7280;">${this.calculateRatingCount(
                        star
                      )}</div>
                    </div>
                  `
                    )
                    .join("")}
                </div>
              </div>
              
              <!-- Top Keywords Summary -->
              <div style="padding: 16px; border: 1px solid #E5E7EB; border-radius: 8px; background-color: #FFFFFF;">
                <h3 style="font-size: 14px; font-weight: 500; color: #4B5563; margin: 0 0 12px 0;">Key Insights</h3>
                
                <div style="margin-bottom: 12px;">
                  <h4 style="font-size: 12px; font-weight: 500; color: #059669; margin-bottom: 8px;">Top Positives</h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 6px;">
                    ${this.topPositiveKeywords
                      .map(
                        (kw) => `
                      <span style="display: inline-block; padding: 4px 8px; color: #065F46; border-radius: 9999px; font-size: 11px;">
                        ${kw.text} (${kw.count})
                      </span>
                    `
                      )
                      .join("")}
                  </div>
                </div>
                
                <div>
                  <h4 style="font-size: 12px; font-weight: 500; color: #DC2626; margin-bottom: 8px;">Top Concerns</h4>
                  <div style="display: flex; flex-wrap: wrap; gap: 6px;">
                    ${this.topNegativeKeywords
                      .map(
                        (kw) => `
                      <span style="display: inline-block; padding: 4px 8px; color: #991B1B; border-radius: 9999px; font-size: 11px;">
                        ${kw.text} (${kw.count})
                      </span>
                    `
                      )
                      .join("")}
                  </div>
                </div>
              </div>
            </div>
          `;
          container.appendChild(summarySection);
        }

        // Add timeline section
        // if (this.reportData?.data) {
        //   const timelineSection = document.createElement("div");
        //   timelineSection.style.marginBottom = "32px";
        //   timelineSection.style.padding = "16px";
        //   timelineSection.style.backgroundColor = "white";
        //   timelineSection.style.borderRadius = "8px";
        //   timelineSection.style.border = "1px solid #E5E7EB";
        //   timelineSection.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.1)";

        //   // Timeline header
        //   timelineSection.innerHTML = `
        //     <h2 style="font-size: 18px; font-weight: 600; color: #1F2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB;">Feedback Timeline</h2>
        //   `;

        //   // Timeline content
        //   const timelineContent = document.createElement("div");
        //   timelineContent.style.position = "relative";
        //   timelineContent.style.padding = "0 8px";

        //   // Add vertical line
        //   const timelineLine = document.createElement("div");
        //   timelineLine.style.position = "absolute";
        //   timelineLine.style.left = "50%";
        //   timelineLine.style.top = "0";
        //   timelineLine.style.bottom = "0";
        //   timelineLine.style.width = "2px";
        //   timelineLine.style.backgroundColor = "#E5E7EB";
        //   timelineLine.style.transform = "translateX(-50%)";

        //   timelineContent.appendChild(timelineLine);

        //   // Add timeline entries
        //   let index = 0;
        //   for (const [month, data] of Object.entries(this.reportData.data)) {
        //     const timelineEntry = document.createElement("div");
        //     timelineEntry.style.display = "flex";
        //     timelineEntry.style.marginBottom = "32px";
        //     timelineEntry.style.position = "relative";
        //     timelineEntry.style.pageBreakInside = "avoid";

        //     // Month marker in center
        //     const monthMarker = document.createElement("div");
        //     monthMarker.style.position = "absolute";
        //     monthMarker.style.left = "50%";
        //     monthMarker.style.transform = "translateX(-50%)";
        //     monthMarker.style.zIndex = "1";
        //     monthMarker.style.backgroundColor = "white";
        //     monthMarker.style.border = "2px solid #4F46E5";
        //     monthMarker.style.borderRadius = "50%";
        //     monthMarker.style.width = "16px";
        //     monthMarker.style.height = "16px";

        //     timelineEntry.appendChild(monthMarker);

        //     // Month label
        //     const monthLabel = document.createElement("div");
        //     monthLabel.style.position = "absolute";
        //     monthLabel.style.left = "50%";
        //     monthLabel.style.transform = "translateX(-50%)";
        //     monthLabel.style.top = "-24px";
        //     monthLabel.style.fontWeight = "500";
        //     monthLabel.style.fontSize = "14px";
        //     monthLabel.style.color = "#374151";
        //     monthLabel.style.backgroundColor = "white";
        //     monthLabel.style.padding = "0 8px";
        //     monthLabel.style.zIndex = "1";
        //     monthLabel.textContent = this.formatMonth(month);

        //     timelineEntry.appendChild(monthLabel);

        //     // Left side (alternating content)
        //     const leftContent = document.createElement("div");
        //     leftContent.style.width = "48%";
        //     leftContent.style.paddingRight = "24px";
        //     leftContent.style.position = "relative";

        //     // Right side (alternating content)
        //     const rightContent = document.createElement("div");
        //     rightContent.style.width = "48%";
        //     rightContent.style.marginLeft = "auto";
        //     rightContent.style.paddingLeft = "24px";
        //     rightContent.style.position = "relative";

        //     // Determine which content goes on which side
        //     const positiveFeedback = data.aggregated?.feedback?.positive || [];
        //     const negativeFeedback = data.aggregated?.feedback?.negative || [];

        //     // Create the metrics card (rating and review count with comparisons)
        //     const metricsCardHTML = this.generateMetricsCardHTML(month, data);

        //     if (index % 2 === 0) {
        //       // Even indexes: Negative on left, Positive on right
        //       leftContent.innerHTML = `
        //         ${metricsCardHTML}
        //         ${this.generateFeedbackHtml(
        //           negativeFeedback,
        //           "Negative Feedback",
        //           false
        //         )}
        //       `;
        //       rightContent.innerHTML = `
        //         ${metricsCardHTML}
        //         ${this.generateFeedbackHtml(
        //           positiveFeedback,
        //           "Positive Feedback",
        //           true
        //         )}
        //       `;
        //     } else {
        //       // Odd indexes: Positive on left, Negative on right
        //       leftContent.innerHTML = `
        //         ${metricsCardHTML}
        //         ${this.generateFeedbackHtml(
        //           positiveFeedback,
        //           "Positive Feedback",
        //           false
        //         )}
        //       `;
        //       rightContent.innerHTML = `
        //         ${metricsCardHTML}
        //         ${this.generateFeedbackHtml(
        //           negativeFeedback,
        //           "Negative Feedback",
        //           true
        //         )}
        //       `;
        //     }

        //     timelineEntry.appendChild(leftContent);
        //     timelineEntry.appendChild(rightContent);
        //     timelineContent.appendChild(timelineEntry);

        //     index++;
        //   }

        //   timelineSection.appendChild(timelineContent);
        //   container.appendChild(timelineSection);
        // }

        // Add the original table data section
        if (this.activeData) {
          const tableContainer = document.createElement("div");
          tableContainer.style.backgroundColor = "#FFFFFF";
          tableContainer.style.borderRadius = "8px";
          tableContainer.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
          tableContainer.style.overflow = "hidden";
          tableContainer.style.pageBreakInside = "avoid";

          tableContainer.innerHTML = `
              <table style="width: 100%; border-collapse: collapse;">
                <thead style="background-color: #F9FAFB; display: table-header-group;">
                  <tr>
                    <th style="padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 500; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB;">Month</th>
                    <th style="padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 500; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB;">Total Reviews</th>
                    <th style="padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 500; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB;">Average Rating</th>
                    <th style="padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 500; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB;">Positive Feedback</th>
                    <th style="padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 500; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB;">Negative Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  ${Object.entries(this.activeData)
                    .map(
                      ([month, data]) => `
                        <tr style="page-break-inside: avoid;">
                          <td style="padding: 16px 24px; font-size: 14px; font-weight: 500; color: #111827; vertical-align: top;">
                            ${this.formatMonth(month)}
                          </td>
                          <td style="padding: 16px 24px; font-size: 14px; color: #6B7280; vertical-align: top;">
                            ${data.aggregated?.totalReviews}
                          </td>
                          <td style="padding: 16px 24px; vertical-align: top;">
                            <div style="display: flex; align-items: center;">
                              <span style="font-size: 14px; font-weight: 500; color: #111827; margin-right: 8px;">
                                ${this.calculateAverageRating(
                                  data.aggregated.ratingDistribution
                                ).toFixed(1)}
                              </span>
                              <div style="color: #F59E0B;">★★★★★</div>
                            </div>
                          </td>
                          <td style="padding: 16px 24px; vertical-align: top;">
                            ${this.formatFeedbackForPDF(
                              data.aggregated.feedback.positive,
                              true
                            )}
                          </td>
                          <td style="padding: 16px 24px; vertical-align: top;">
                            ${this.formatFeedbackForPDF(
                              data.aggregated.feedback.negative,
                              false
                            )}
                          </td>
                        </tr>
                        ${
                          data.aggregated.keywords?.length > 0 ||
                          data.aggregated.stf?.length > 0
                            ? `
                              <tr style="background-color: #F9FAFB; page-break-inside: avoid;">
                                <td colspan="5" style="padding: 12px 24px;">
                                  ${
                                    data.aggregated.keywords?.length > 0
                                      ? `
                                    <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: ${data.aggregated.stf?.length > 0 ? '12px' : '0'};">
                                      <span style="font-size: 12px; font-weight: 500; color: #6B7280; margin-right: 8px;">Review Highlights:</span>
                                      ${data.aggregated.keywords
                                        .map(
                                          (keyword) => `
                                            <span style="
                                              display: inline-block;
                                              padding: 4px 8px;
                                              border-radius: 9999px;
                                              font-size: 12px;
                                              font-weight: 500;
                                              margin: 2px;
                                              ${
                                                keyword.type === "positive"
                                                  ? "background-color: #DEF7EC; color: #03543F;"
                                                  : keyword.type === "negative"
                                                  ? "background-color: #FDE8E8; color: #9B1C1C;"
                                                  : "background-color: #E1EFFE; color: #1E429F;"
                                              }
                                            ">
                                              ${keyword.text} (${keyword.count})
                                            </span>
                                          `
                                        )
                                        .join("")}
                                    </div>`
                                      : ""
                                  }
                                  ${
                                    data.aggregated.stf?.length > 0
                                      ? `
                                    <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
                                      <span style="font-size: 12px; font-weight: 500; color: #1D4ED8; margin-right: 8px;">Staff Mentions:</span>
                                      ${[...data.aggregated.stf]
                                        .sort((a, b) => b.count - a.count)
                                        .slice(0, 5)
                                        .map(
                                          (staff) => `
                                            <span style="
                                              display: inline-block;
                                              padding: 4px 8px;
                                              border-radius: 9999px;
                                              font-size: 12px;
                                              font-weight: 500;
                                              margin: 2px;
                                              background-color: #DBEAFE; 
                                              color: #1E40AF;
                                            ">
                                              ${staff.name} (${staff.count})
                                            </span>
                                          `
                                        )
                                        .join("")}
                                      ${data.aggregated.stf.length > 5 ? `
                                        <span style="
                                          display: inline-block;
                                          padding: 4px 8px;
                                          border-radius: 9999px;
                                          font-size: 12px;
                                          font-weight: 500;
                                          margin: 2px;
                                          background-color: #E5E7EB; 
                                          color: #6B7280;
                                        ">
                                          +${data.aggregated.stf.length - 5} more
                                        </span>
                                      ` : ''}
                                    </div>`
                                      : ""
                                  }
                                </td>
                              </tr>
                            `
                            : ""
                        }
                      `
                    )
                    .join("")}
                </tbody>
              </table>
            `;
          container.appendChild(tableContainer);
        }

        const opt = {
          margin: [15, 15],
          filename: `${this.currentWorkspaceName}-gmb-reviews-report-${
            new Date().toISOString().split("T")[0]
          }.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, logging: false, useCORS: true },
          jsPDF: {
            unit: "mm",
            format: "a3",
            orientation: "portrait",
            compress: true,
          },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        };

        await html2pdf().set(opt).from(container).save();
      } catch (error) {
        console.error("Error downloading report:", error);
        this.$alert.show("Failed to download report");
      } finally {
        // Hide header after PDF generation
        this.showScreenshotHeader = false;
        this.isDownloading = false;
      }
    },

    // Helper method for generating feedback HTML for the timeline in the PDF
    generateFeedbackHtml(feedback, title, isRight) {
      const alignment = isRight ? "left" : "right";
      const bgColor = title.includes("Positive") ? "#D1FAE5" : "#FEE2E2";
      const borderColor = title.includes("Positive") ? "#A7F3D0" : "#FECACA";
      const textColor = title.includes("Positive") ? "#065F46" : "#991B1B";

      return `
        <div style="background-color: ${bgColor}; border: 1px solid ${borderColor}; border-radius: 8px; padding: 12px; text-align: ${alignment};">
          <h4 style="font-size: 14px; font-weight: 500; color: ${textColor}; margin-top: 0; margin-bottom: 8px;">${title}</h4>
          ${
            feedback.length > 0
              ? `<ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
                ${feedback
                  .slice(0, 5)
                  .map(
                    (point) => `
                  <li style="color: ${textColor}; font-size: 12px; margin-bottom: 4px;">
                    ${point}
                  </li>
                `
                  )
                  .join("")}
              </ul>`
              : `<p style="color: #6B7280; font-size: 12px; margin: 0;">No feedback available</p>`
          }
        </div>
      `;
    },

    // Helper method for generating metrics card HTML for the timeline in the PDF
    generateMetricsCardHTML(month, data) {
      const currentRating = this.calculateAverageRating(
        data.aggregated.ratingDistribution
      );
      const currentReviews = data.aggregated?.totalReviews || 0;

      const previousRating = this.getPreviousMonthRating(month);
      const previousReviews = this.getPreviousMonthValue(month, "totalReviews");

      // Calculate percentage changes
      let ratingChange = 0;
      let reviewsChange = 0;

      if (previousRating !== null && previousRating !== 0) {
        ratingChange =
          ((currentRating - previousRating) / previousRating) * 100;
      }

      if (previousReviews !== null && previousReviews !== 0) {
        reviewsChange =
          ((currentReviews - previousReviews) / previousReviews) * 100;
      }

      // Create comparison indicators
      const getChangeHTML = (change) => {
        if (change === null || Math.abs(change) < 0.5) return "";

        const absChange = Math.abs(change);
        const formattedChange = `${change > 0 ? "+" : ""}${absChange.toFixed(
          1
        )}%`;

        let color, icon;
        if (change > 0) {
          color = "#10B981";
          icon = "↑";
        } else if (change < 0) {
          color = "#EF4444";
          icon = "↓";
        } else {
          color = "#9CA3AF";
          icon = "–";
        }

        return `<span style="color: ${color}; font-size: 10px; margin-left: 4px;">${icon} ${formattedChange}</span>`;
      };

      return `
        <div style="background-color: #EEF2FF; border-radius: 8px; padding: 10px; margin-bottom: 12px; border: 1px solid #E0E7FF;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <div style="font-size: 12px; font-weight: 500; color: #4F46E5;">Reviews&nbsp;</div>
            <div style="display: flex; align-items: center;">
              <span style="font-size: 12px; font-weight: 600; color: #1F2937;">${currentReviews}</span>
              ${getChangeHTML(reviewsChange)}
            </div>
          </div>
          <div style="display: flex; justify-content: space-between;">
              <div style="font-size: 12px; font-weight: 500; color: #4F46E5;">Rating &nbsp;</div>
            <div style="display: flex; align-items: center;">
              <span style="font-size: 12px; font-weight: 600; color: #1F2937;">${currentRating.toFixed(
                1
              )}</span>
              ${getChangeHTML(ratingChange)}
            </div>
          </div>
        </div>
      `;
    },

    formatFeedbackForPDF(feedback, isPositive) {
      if (!Array.isArray(feedback) || feedback.length === 0) {
        return '<span style="color: #6B7280; font-size: 14px;">No feedback available</span>';
      }
      let num = 5;
      if (this.selectedMonth != "all") {
        num = feedback.length;
      }
      return `
        <ul style="margin: 0; padding-left: 16px; list-style-type: disc;">
            ${feedback
              .slice(0, num)
              .map(
                (point) => `
                <li style="color: ${
                  isPositive ? "#059669" : "#DC2626"
                }; font-size: 14px; margin-bottom: 4px; white-space: normal; overflow-wrap: break-word; word-wrap: break-word;">
                    ${point}
                </li>
            `
              )
              .join("")}
            ${
              feedback.length > num
                ? `<li style="color: #6B7280; font-size: 12px; font-style: italic; margin-top: 4px;">+ ${
                    feedback.length - num
                  } more...</li>`
                : ""
            }
        </ul>
      `;
    },

    async sendReport() {
      this.isSending = true;
      try {
        const html2pdf = (await import("html2pdf.js")).default;
        const container = document.createElement("div");
        container.style.padding = "20px";
        container.style.fontFamily = "Arial, sans-serif";

        const title = this.selectedStore
          ? `GMB Reviews Report - ${this.selectedStore.sub_locality}`
          : "GMB Reviews Report - All Locations";
        container.innerHTML = `
      <h1 style="color: #1a202c; margin-bottom: 20px; font-size: 24px;">${title}</h1>
      <p style="color: #4a5568; margin-bottom: 30px;">Report generated on ${new Date().toLocaleDateString()}</p>
    `;

        if (this.reportData?.data) {
          Object.entries(this.reportData.data).forEach(([month, data]) => {
            const monthSection = document.createElement("div");
            monthSection.style.marginBottom = "40px";
            monthSection.innerHTML = `
          <h2 style="color: #2d3748; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #e2e8f0; font-size: 20px;">
            ${this.formatMonth(month)}
          </h2>
          <div style="margin-bottom: 20px;">
            <p style="margin-bottom: 10px;">
              <strong>Total Reviews:</strong> ${data.aggregated?.totalReviews}
            </p>
            <p style="margin-bottom: 20px;">
              <strong>Average Rating:</strong> ${this.calculateAverageRating(
                data.aggregated.ratingDistribution
              ).toFixed(1)} ⭐
            </p>
          </div>
          <div style="margin-bottom: 20px;">
            <h3 style="color: #2f855a; margin-bottom: 10px; font-size: 16px;">Positive Feedback</h3>
            ${this.formatFeedbackForPDF(
              data.aggregated.feedback.positive,
              "green"
            )}
          </div>
          <div style="margin-bottom: 20px;">
            <h3 style="color: #c53030; margin-bottom: 10px; font-size: 16px;">Negative Feedback</h3>
            ${this.formatFeedbackForPDF(
              data.aggregated.feedback.negative,
              "red"
            )}
          </div>
        `;
            container.appendChild(monthSection);
          });
        }

        const opt = {
          margin: [15, 15],
          filename: `gmb-reviews-report-${
            new Date().toISOString().split("T")[0]
          }.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, logging: false, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        };

        const pdfBlob = await html2pdf()
          .set(opt)
          .from(container)
          .output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const shareOptions = document.createElement("div");
        shareOptions.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 class="text-lg font-medium mb-4">Share Report</h3>
          <div class="space-y-4">
            <button class="w-full py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-2">
              <i class="mdi mdi-email"></i>
              Send via Email
            </button>
            <button class="w-full py-2 px-4 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2">
              <i class="mdi mdi-whatsapp"></i>
              Share via WhatsApp
            </button>
            <button class="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center justify-center gap-2">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;
        document.body.appendChild(shareOptions);

        const buttons = shareOptions.getElementsByTagName("button");
        buttons[0].onclick = () => {
          const emailSubject = encodeURIComponent(title);
          const emailBody = encodeURIComponent(
            `Please find attached the GMB Reviews Insights generated on ${new Date().toLocaleDateString()}.`
          );
          const mailtoLink = `mailto:?subject=${emailSubject}&body=${emailBody}&attachment=${pdfUrl}`;
          window.open(mailtoLink);
          document.body.removeChild(shareOptions);
        };
        buttons[1].onclick = () => {
          const whatsappText = encodeURIComponent(
            `${title}\n\nGenerated on ${new Date().toLocaleDateString()}`
          );
          const whatsappLink = `https://wa.me/?text=${whatsappText}&attachment=${pdfUrl}`;
          window.open(whatsappLink);
          document.body.removeChild(shareOptions);
        };
        buttons[2].onclick = () => {
          document.body.removeChild(shareOptions);
        };

        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      } catch (error) {
        console.error("Error sharing report:", error);
        this.$alert.show("Failed to share report");
      } finally {
        this.isSending = false;
      }
    },

    clearStoreSelection() {
      this.selectedStore = null;
      this.selectedWID = null;
      this.locationSearch = "";
      this.showStoreDropdown = false;
      this.selectedRatingRange = null; // Reset rating filter
    },

    getKeywordClass(sentiment) {
      switch (sentiment?.toLowerCase()) {
        case "positive":
          return "bg-green-100 text-green-800";
        case "negative":
          return "bg-red-100 text-red-800";
        case "neutral":
          return "bg-blue-100 text-blue-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },

    loadMoreStores() {
      this.storesToShow += 20; // Increase the number of stores to show by 20
    },

    limitKeywords(keywords) {
      if (!keywords || !Array.isArray(keywords)) return [];
      return [...keywords]
        .sort((a, b) => {
          // First sort by type (positive, neutral, negative)
          const typeOrder = { positive: 1, neutral: 2, negative: 3 };
          const typeCompare = typeOrder[a.type] - typeOrder[b.type];

          // If same type, sort by count descending
          if (typeCompare === 0) {
            return b.count - a.count;
          }
          return typeCompare;
        })
        .slice(0, 10);
    },

    limitFeedback(feedback) {
      if (!feedback || !Array.isArray(feedback)) return [];
      return feedback.slice(0, 5);
    },

    showAllKeywords(keywords, month) {
      console.log("Receving this data in showkeywords ", keywords);
      if (!keywords || !Array.isArray(keywords)) return;

      this.allKeywords = [...keywords].sort((a, b) => {
        // First sort by type (positive, neutral, negative)
        const typeOrder = { positive: 1, neutral: 2, negative: 3 };
        const typeCompare = typeOrder[a.type] - typeOrder[b.type];

        // If same type, sort by count descending
        if (typeCompare === 0) {
          return b.count - a.count;
        }
        return typeCompare;
      });

      this.selectedMonth = month;
      this.showAllKeywordsModal = true;
    },

    showAllFeedback(feedback, type, month) {
      if (!feedback || !Array.isArray(feedback)) return;
      this.allFeedback = feedback;
      this.feedbackType = type;
      // this.selectedMonth = month;
      this.showAllFeedbackModal = true;
    },

    showAllStaffMentions(staffMentions, month) {
      if (!staffMentions || !Array.isArray(staffMentions)) return;

      // Sort staff mentions by count (highest first), then alphabetically by name
      this.allStaffMentions = [...staffMentions].sort((a, b) => {
        // First sort by count in descending order
        const countDiff = b.count - a.count;
        if (countDiff !== 0) return countDiff;
        // If counts are equal, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });

      this.selectedMonth = month;
      this.showAllStaffMentionsModal = true;
    },

    // Show locations for a specific staff member (similar to showLocationsForKeyword)
    showLocationsForStaffMember(staff, month) {
      console.log(`🔍 Searching locations for staff: "${staff.name}" in month: ${month || 'all'}`);
      
      // Get the month to use - if no specific month provided, aggregate from all months
      const currentMonth = month || Object.keys(this.reportData.data)[0];

      // If no specific month provided, aggregate location data from all months
      if (!month && this.reportData?.staffLocationMap) {
        const allMonthsLocations = [];
        const processedLocationIds = new Set();

        // Iterate through all months
        Object.keys(this.reportData.staffLocationMap).forEach((monthKey) => {
          if (this.reportData.staffLocationMap[monthKey][staff.name]) {
            // For each location in this month for this staff member
            this.reportData.staffLocationMap[monthKey][staff.name].forEach(
              (location) => {
                if (!processedLocationIds.has(location.id)) {
                  allMonthsLocations.push({ ...location });
                  processedLocationIds.add(location.id);
                } else {
                  // Update count for existing location
                  const existingLocation = allMonthsLocations.find(
                    (loc) => loc.id === location.id
                  );
                  if (existingLocation) {
                    existingLocation.count += location.count;
                    existingLocation.reviews = [
                      ...new Set([
                        ...(existingLocation.reviews || []),
                        ...location.reviews,
                      ]),
                    ];
                  }
                }
              }
            );
          }
        });

        // Sort by count (highest first)
        allMonthsLocations.sort((a, b) => b.count - a.count);

        this.selectedLocations = allMonthsLocations;
        this.selectedKeyword = { text: staff.name, type: 'staff' }; // Reuse existing modal
        this.showLocationModal = true;
        return;
      }

      // For specific month, get staff location data  
      const availableStaffNames = Object.keys(this.reportData?.staffLocationMap?.[currentMonth] || {});
      console.log(`📅 Month: ${currentMonth}, Available staff: [${availableStaffNames.join(', ')}]`);
      
      if (
        this.reportData?.staffLocationMap &&
        this.reportData.staffLocationMap[currentMonth] &&
        this.reportData.staffLocationMap[currentMonth][staff.name]
      ) {
        this.selectedLocations = this.reportData.staffLocationMap[currentMonth][staff.name];
        console.log("Found locations for staff:", this.selectedLocations);
      } else {
        console.warn(`❌ No locations found for "${staff.name}"`);
        console.log(`🔍 Available: [${availableStaffNames.join('", "')}]`);
        console.log(`📏 Name lengths - Searching: "${staff.name}" (${staff.name.length}), Available:`, 
          availableStaffNames.map(n => `"${n}" (${n.length})`));
        
        this.selectedLocations = [];
      }

      this.selectedKeyword = { text: staff.name, type: 'staff' }; // Reuse existing location modal
      this.showLocationModal = true;
    },

    // Format month as abbreviated version (e.g., "Jan '23")
    formatMonthAbbrev(monthStr) {
      const [year, month] = monthStr.split("-");
      return new Date(year, month - 1).toLocaleString("default", {
        month: "short",
        year: "2-digit",
      });
    },

    // Calculate overall rating across all months
    calculateOverallRating() {
      if (!this.activeData) return 0;

      let totalRating = 0;
      let totalCount = 0;

      Object.values(this.activeData).forEach((data) => {
        const monthRating = this.calculateAverageRating(
          data.aggregated.ratingDistribution
        );
        const monthCount = data.aggregated.totalReviews || 0;

        totalRating += monthRating * monthCount;
        totalCount += monthCount;
      });

      return totalCount > 0 ? totalRating / totalCount : 0;
    },

    calculateTotalReviews() {
      if (!this.activeData) return 0;

      return Object.values(this.activeData).reduce((total, data) => {
        return total + (data.aggregated.totalReviews || 0);
      }, 0);
    },

    // Calculate rating percentage for a given star level
    calculateRatingPercentage(star) {
      if (!this.activeData) return 0;

      let totalStarCount = 0;
      let totalCount = 0;

      Object.values(this.activeData).forEach((data) => {
        const distribution = data.aggregated.ratingDistribution || {};
        totalStarCount += distribution[star] || 0;
        totalCount += Object.values(distribution).reduce(
          (sum, count) => sum + count,
          0
        );
      });

      return totalCount > 0 ? (totalStarCount / totalCount) * 100 : 0;
    },

    // Calculate total count for a given star rating
    calculateRatingCount(star) {
      if (!this.activeData) return 0;

      return Object.values(this.activeData).reduce((total, data) => {
        const distribution = data.aggregated.ratingDistribution || {};
        return total + (distribution[star] || 0);
      }, 0);
    },

    // Generate data points for rating trend visualization
    generateRatingTrendPoints() {
      if (!this.activeData || Object.keys(this.activeData).length === 0) {
        return "";
      }

      // Get months sorted chronologically
      const months = Object.keys(this.activeData)
        .sort((a, b) => new Date(a) - new Date(b))
        .slice(-6); // Only show last 6 months

      const ratings = months.map((month) => ({
        month,
        rating: this.calculateAverageRating(
          this.activeData[month].aggregated.ratingDistribution
        ),
      }));

      // Calculate SVG dimensions
      const width = 300;
      const height = 100;
      const padding = 10;
      const availableWidth = width - padding * 2;
      const availableHeight = height - padding * 2;

      // Generate points for the trend line
      return ratings
        .map((item, index) => {
          const x =
            index * (availableWidth / (ratings.length - 1 || 1)) + padding;
          const y = height - (item.rating * availableHeight) / 5 - padding;
          return `${x},${y}`;
        })
        .join(" ");
    },

    // Generate top keywords for the summary section
    generateTopKeywords() {
      if (!this.activeData) return;

      const allPositive = [];
      const allNegative = [];

      // helper: deduplicate while preserving order
      const mergeUnique = (a = [], b = []) => [...new Set([...a, ...b])];

      Object.values(this.activeData).forEach((data) => {
        const keywords = data.aggregated?.keywords || [];

        keywords.forEach((kw) => {
          // choose the target array first
          const target =
            kw.type === "positive"
              ? allPositive
              : kw.type === "negative"
              ? allNegative
              : null;

          if (!target) return; // skip neutral or unknown types

          const existing = target.find((k) => k.text === kw.text);

          if (existing) {
            // ── aggregate fields ───────────────────────────────
            existing.count += kw.count;
            existing.reviews = mergeUnique(existing.reviews, kw.reviews);
            // console.log("After Merging this is data ",kw.text, existing.count, existing.reviews);
          } else {
            // ensure we always store a reviews array
            target.push({
              ...kw,
              reviews: [...(kw.reviews || [])],
            });
          }
        });
      });

      // Sort and limit to top 5
      this.topPositiveKeywords = [...allPositive]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      this.topNegativeKeywords = [...allNegative]
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      console.log(
        "this is the aggregated keywords type ",
        this.topPositiveKeywords
      );
    },

    // Generate top staff mentions for the summary section
    generateTopStaffMentions() {
      if (!this.activeData) {
        this.topStaffMentions = [];
        return;
      }

      const allStaffMentions = [];

      // helper: deduplicate while preserving order
      const mergeUnique = (a = [], b = []) => [...new Set([...a, ...b])];

      Object.values(this.activeData).forEach((data) => {
        const staffMentions = data.aggregated?.stf || [];

        staffMentions.forEach((staff) => {
          // Validate staff object
          if (!staff || !staff.name || typeof staff.name !== 'string') {
            console.warn("Invalid staff mention found:", staff);
            return;
          }

          const existing = allStaffMentions.find((s) => s.name === staff.name);

          if (existing) {
            // Aggregate fields with validation
            existing.count += (staff.count || 0);
            existing.reviewIds = mergeUnique(
              existing.reviewIds || [],
              staff.reviewIds || []
            );
          } else {
            // Add new staff mention with validated data
            allStaffMentions.push({
              name: staff.name,
              count: staff.count || 0,
              reviewIds: Array.isArray(staff.reviewIds) ? [...staff.reviewIds] : [],
            });
          }
        });
      });

      // Sort and limit to top 5, filter out invalid entries
      this.topStaffMentions = allStaffMentions
        .filter(staff => staff.name && staff.count > 0)
        .sort((a, b) => {
          // First sort by count in descending order
          const countDiff = (b.count || 0) - (a.count || 0);
          if (countDiff !== 0) return countDiff;
          // If counts are equal, sort alphabetically by name
          return a.name.localeCompare(b.name);
        })
        .slice(0, 5);

      console.log("Top staff mentions aggregated: ", this.topStaffMentions);
    },

    // Calculate the overall brand sentiment based on positive and negative keywords
    calculateBrandSentiment() {
      if (!this.topPositiveKeywords || !this.topNegativeKeywords) return 5.0;

      const totalPositive = this.topPositiveKeywords.reduce(
        (sum, kw) => sum + kw.count,
        0
      );
      const totalNegative = this.topNegativeKeywords.reduce(
        (sum, kw) => sum + kw.count,
        0
      );

      const total = totalPositive + totalNegative;
      if (total === 0) return 5.0;

      // Calculate a weighted sentiment score (1-10 scale)
      const positiveWeight = totalPositive / total;
      return Math.min(10, Math.max(1, positiveWeight * 10));
    },

    // Calculate the sentiment arc for the gauge visualization (0-283 for the circle circumference)
    calculateSentimentArc() {
      const sentiment = this.calculateBrandSentiment();
      const percentage = (sentiment - 1) / 9; // Convert 1-10 scale to 0-1
      return Math.round(percentage * 283); // 283 is the circumference of a circle with r=45
    },

    // Get color based on sentiment score
    getSentimentColor(sentiment) {
      if (sentiment >= 8) return "#10B981"; // Green
      if (sentiment >= 6) return "#6366F1"; // Indigo
      if (sentiment >= 4) return "#F59E0B"; // Amber
      if (sentiment >= 2) return "#EF4444"; // Red
      return "#EF4444"; // Default red
    },

    // Generate a text summary based on the sentiment
    generateBrandSummary() {
      const sentiment = this.calculateBrandSentiment();
      const reviewCount = this.calculateTotalReviews();
      const rating = this.calculateOverallRating().toFixed(1);

      if (sentiment >= 8) {
        return `Your brand is perceived very positively with an average rating of ${rating} stars from ${reviewCount} reviews. Customers frequently highlight ${this.topPositiveKeywords
          .slice(0, 2)
          .map((k) => k.text.toLowerCase())
          .join(" and ")} as key strengths.`;
      } else if (sentiment >= 6) {
        return `Your brand is generally well-received with an average rating of ${rating} stars from ${reviewCount} reviews. While customers appreciate ${this.topPositiveKeywords[0]?.text.toLowerCase()}, there are some concerns about ${this.topNegativeKeywords[0]?.text.toLowerCase()}.`;
      } else if (sentiment >= 4) {
        return `Your brand receives mixed feedback with an average rating of ${rating} stars from ${reviewCount} reviews. Address concerns about ${this.topNegativeKeywords
          .slice(0, 2)
          .map((k) => k.text.toLowerCase())
          .join(
            " and "
          )} while leveraging strengths like ${this.topPositiveKeywords[0]?.text.toLowerCase()}.`;
      } else {
        return `Your brand faces some challenges with an average rating of ${rating} stars from ${reviewCount} reviews. Focus on improving ${this.topNegativeKeywords
          .slice(0, 2)
          .map((k) => k.text.toLowerCase())
          .join(" and ")} to enhance customer satisfaction.`;
      }
    },

    // Generate insights grouped by location
    async generateLocationInsights() {
      if (!this.stores || this.stores.length === 0) {
        await this.fetchStores(); // Ensure stores are loaded
      }

      if (
        !this.effectiveReportData?.data ||
        !this.effectiveReportData.keywordLocationMap
      )
        return;
      console.log("This was the locaiton insights data ", this.effectiveReportData?.keywordLocationMap);
      const locationInsights = []; // This will be this.locationInsights

      // Get the months we want to process based on selection
      const monthsToProcess =
        this.selectedMonth === "all"
          ? Object.keys(this.effectiveReportData.keywordLocationMap)
          : [this.selectedMonth];

      monthsToProcess.forEach((month) => {
        const keywordMap = this.effectiveReportData.keywordLocationMap[month];
        if (!keywordMap) return;

        Object.entries(keywordMap).forEach(([keywordKey, locationsData]) => {
          const [keywordText, keywordType] = keywordKey.split("-");

          locationsData.forEach((locationEntry) => {
            const gmbIdFromReport = locationEntry.id;

            if (!gmbIdFromReport) {
              console.warn("Undefined locationId found in keyword map");
              return;
            }

            let insight = locationInsights.find(
              (item) => item.id === gmbIdFromReport
            );

            if (!insight) {
              const locationName = this.findLocationName(gmbIdFromReport);

              const storeForAreaCity = this.stores.find(
                (s) =>
                  s.id === gmbIdFromReport ||
                  s.wid === gmbIdFromReport ||
                  s.locationId === gmbIdFromReport ||
                  s.placeId === gmbIdFromReport
              );

              let areaCityDisplay = "";
              if (storeForAreaCity) {
                const parts = [];
                if (storeForAreaCity.sub_locality)
                  parts.push(storeForAreaCity.sub_locality);
                if (storeForAreaCity.city) parts.push(storeForAreaCity.city);
                areaCityDisplay = parts.join(", ");
              }

              const fullAddressForInsight =
                this.findLocationAddress(gmbIdFromReport);

              insight = {
                id: gmbIdFromReport,
                locationId: gmbIdFromReport,  // Add locationId for compatibility
                name: locationName,
                address: fullAddressForInsight,
                areaCity: areaCityDisplay,
                positiveKeywords: [],
                negativeKeywords: [],
                positiveCount: 0,
                negativeCount: 0,
                sentimentScore: 5.0,
              };
              locationInsights.push(insight);
            }

            const keywordCountForLocation = locationEntry.count;
            const keywordReviewsForLocation = locationEntry.reviews;
            const targetArray =
              keywordType === "positive"
                ? insight.positiveKeywords
                : keywordType === "negative"
                ? insight.negativeKeywords
                : null;

            if (targetArray) {
              const existingKeyword = targetArray.find(
                (kw) => kw.text === keywordText
              );
              if (existingKeyword) {
                existingKeyword.count += keywordCountForLocation;
                existingKeyword.reviews = [
                  ...new Set([
                    ...(existingKeyword.reviews || []),
                    ...keywordReviewsForLocation,
                  ]),
                ];
              } else {
                targetArray.push({
                  text: keywordText,
                  type: keywordType,
                  count: keywordCountForLocation,
                  reviews: keywordReviewsForLocation,
                });
              }

              if (keywordType === "positive") {
                insight.positiveCount += keywordCountForLocation;
              } else if (keywordType === "negative") {
                insight.negativeCount += keywordCountForLocation;
              }
            }
          });
        });
      });

      // Calculate final sentiment scores for each location
      locationInsights.forEach((insight) => {
        const totalKeywordMentions =
          insight.positiveCount + insight.negativeCount;
        if (totalKeywordMentions > 0) {
          const positiveRatio = insight.positiveCount / totalKeywordMentions;
          insight.sentimentScore = 1 + positiveRatio * 9;
        } else {
          insight.sentimentScore = 5.0;
        }

        // Sort keywords by count
        insight.positiveKeywords.sort((a, b) => b.count - a.count);
        insight.negativeKeywords.sort((a, b) => b.count - a.count);
      });

      this.locationInsights = locationInsights;
      this.sortLocationInsights();
    },

    // Sort location insights based on the current sort option
    sortLocationInsights() {
      if (!this.locationInsights || this.locationInsights.length === 0) return;

      if (this.locationSortOption === "positive") {
        this.locationInsights.sort((a, b) => b.positiveCount - a.positiveCount);
      } else if (this.locationSortOption === "negative") {
        this.locationInsights.sort((a, b) => b.negativeCount - a.negativeCount);
      } else if (this.locationSortOption === "sentiment_high") {
        this.locationInsights.sort(
          (a, b) => (b.sentimentScore || 0) - (a.sentimentScore || 0)
        );
      } else if (this.locationSortOption === "sentiment_low") {
        this.locationInsights.sort(
          (a, b) => (a.sentimentScore || 0) - (b.sentimentScore || 0)
        );
      }
    },

    // Get the value from the previous month for comparison
    getPreviousMonthValue(currentMonth, field) {
      if (!this.reportData?.data) return null;

      // Get months array sorted chronologically
      const months = Object.keys(this.reportData.data).sort();
      const currentIndex = months.indexOf(currentMonth);

      // Return null if this is the first month or invalid month
      if (currentIndex <= 0) return null;

      // Get the previous month's data
      const previousMonth = months[currentIndex - 1];
      const previousData = this.reportData.data[previousMonth];

      // Return the appropriate field value
      return previousData?.aggregated?.[field] || null;
    },

    // Get the average rating from the previous month for comparison
    getPreviousMonthRating(currentMonth) {
      if (!this.reportData?.data) return null;

      // Get months array sorted chronologically
      const months = Object.keys(this.reportData.data).sort();
      const currentIndex = months.indexOf(currentMonth);

      // Return null if this is the first month or invalid month
      if (currentIndex <= 0) return null;

      // Get the previous month's data
      const previousMonth = months[currentIndex - 1];
      const previousData = this.reportData.data[previousMonth];

      // Calculate and return the previous month's average rating
      if (previousData?.aggregated?.ratingDistribution) {
        return this.calculateAverageRating(
          previousData.aggregated.ratingDistribution
        );
      }

      return null;
    },

    // Safe method to compute and update rating trend data outside of render cycle
    updateRatingTrendData() {
      if (!this.activeData || Object.keys(this.activeData).length === 0) {
        this.ratingTrendData = [];
        return;
      }

      // Get months sorted chronologically
      const months = Object.keys(this.activeData)
        .sort((a, b) => new Date(a) - new Date(b))
        .slice(-6); // Only show last 6 months

      this.ratingTrendData = months.map((month) =>
        this.calculateAverageRating(
          this.activeData[month].aggregated.ratingDistribution
        )
      );
    },

    debugTable() {
      console.log("===== DEBUG TABLE DATA =====");
      console.log("Selected Time Period:", this.selectedTimePeriod);

      if (!this.reportData?.data) {
        console.error("No report data available");
        alert("No report data available. Please generate a report first.");
        return;
      }

      const months = Object.keys(this.reportData.data);
      console.log("Total months in data:", months.length);
      console.log("Month keys:", months);

      // Print the structure of the first month's data to understand what we're working with
      if (months.length > 0) {
        const sampleMonth = this.reportData.data[months[0]];
        console.log("Sample month data structure:", sampleMonth);
      }

      console.log("Current table data length:", this.monthlyTableData.length);
      console.log("Current table data:", this.monthlyTableData);

      // Try to regenerate the table data
      this.isTableLoading = true;

      // Process with a delay to ensure UI updates
      setTimeout(() => {
        this.updateMonthlyTableData();
        console.log(
          "Table data regenerated:",
          this.monthlyTableData.length,
          "rows"
        );
        this.isTableLoading = false;

        if (this.monthlyTableData.length === 0) {
          alert("Unable to generate table data. Check console for details.");
        } else {
          alert(
            `Table data generated with ${this.monthlyTableData.length} months.`
          );
        }
      }, 500);
    },

    // New method to format cache expiration time
    formatCacheExpiration() {
      if (!this.cacheExpiresAt) return "Unknown";

      const expiresDate = new Date(this.cacheExpiresAt);
      return expiresDate.toLocaleString();
    },

    // New method to check if the cache is expired
    isCacheExpired() {
      if (!this.cacheExpiresAt) return true;
      return new Date() >= new Date(this.cacheExpiresAt);
    },

    // New method to setup the cache refresh timer
    setupCacheRefreshTimer() {
      if (this.cacheRefreshInterval) {
        clearInterval(this.cacheRefreshInterval);
      }

      // Check cache status every minute
      this.cacheRefreshInterval = setInterval(() => {
        if (
          this.isCacheExpired() &&
          !this.isBackgroundRefreshing &&
          !this.isGenerating
        ) {
          this.refreshInBackground();
        }
      }, 60000); // Check every minute
    },

    // New method to refresh data in the background
    async refreshInBackground() {
      if (this.isBackgroundRefreshing || this.isGenerating) return;

      this.isBackgroundRefreshing = true;
      try {
        // Call the generate report function but don't show loading state for the entire UI
        await this.generateReportInternal(true);

        // Show a subtle notification for background refresh completion
        const notification = document.createElement("div");
        notification.className =
          "fixed bottom-4 right-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded shadow-md z-50 transition-opacity duration-500";
        notification.innerHTML = `
          <div class="flex items-center">
            <v-icon color="info" small class="mr-2">mdi-refresh</v-icon>
            <span>Report refreshed in background</span>
          </div>
        `;

        document.body.appendChild(notification);

        // Fade out and remove after 3 seconds
        setTimeout(() => {
          notification.style.opacity = "0";
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 500);
        }, 3000);
      } catch (error) {
        console.error("Background refresh failed:", error);
        // Don't show error to user as this is a background process
      } finally {
        this.isBackgroundRefreshing = false;
      }
    },

    // Modified to use localStorage instead of sessionStorage to persist cache longer
    saveToCache() {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + this.cacheExpirationTime);

      const cacheData = {
        selectedWIDs: this.selectedWIDs,
        selectedRatingRange: this.selectedRatingRange,
        reportData: this.reportData,
        locationMonthlyDataMap: this.locationMonthlyDataMap,
        lastGeneratedAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
      };

      localStorage.setItem("gmbReviewReportCache", JSON.stringify(cacheData));

      // Update local state
      this.cacheExpiresAt = expiresAt.toISOString();
      this.lastGeneratedAt = this.formatTimeAgo(cacheData.lastGeneratedAt);

      // Setup the refresh timer
      this.setupCacheRefreshTimer();
    },

    // Load data from cache
    loadFromCache() {
      try {
        const cachedData = localStorage.getItem("gmbReviewReportCache");
        if (!cachedData) {
          // Display a message to the user that they need to generate a report
          this.$nextTick(() => {
            // Only show this message if the component is still mounted
            if (this.$el) {
              const notification = document.createElement("div");
              notification.className =
                "fixed top-4 right-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded shadow-md z-50 transition-opacity duration-500";
              notification.innerHTML = `
                <div class="flex items-center">
                  <v-icon color="info" small class="mr-2">mdi-information</v-icon>
                  <span>No report data found. Please click "Refresh Data" to generate a report.</span>
                </div>
              `;

              document.body.appendChild(notification);

              // Fade out and remove after 6 seconds
              setTimeout(() => {
                notification.style.opacity = "0";
                setTimeout(() => {
                  if (document.body.contains(notification)) {
                    document.body.removeChild(notification);
                  }
                }, 500);
              }, 6000);
            }
          });
          return false;
        }

        const cache = JSON.parse(cachedData);

        // Check if cache is valid and not expired
        if (!cache.expiresAt) return false;

        // Check if cache has staffLocationMap - if not, cache is outdated
        if (!cache.reportData?.staffLocationMap) {
          console.log("Cache is missing staffLocationMap, treating as expired");
          return false;
        }
        // Check if the URL location parameter is different from cached locations
        const locationParam = this.$route.query.location;
        if (locationParam) {
          const locationIds = Array.isArray(locationParam)
            ? locationParam
            : [locationParam];
          const locationIdsArray =
            locationIds.length === 1 && locationIds[0].includes(",")
              ? locationIds[0].split(",").map((id) => id.trim())
              : locationIds;

          // Compare URL locations with cached locations
          const cachedLocations = cache.selectedWIDs || [];
          const hasLocationChanged =
            locationIdsArray.some((id) => !cachedLocations.includes(id)) ||
            cachedLocations.length !== locationIdsArray.length;

          // If locations from URL are different than cached locations, don't use cache
          if (hasLocationChanged) {
            console.log(
              "Location in URL is different from cache, generating new report"
            );
            return false;
          }
        }

        // Load cached data
        this.selectedWIDs = cache.selectedWIDs || [];
        this.selectedRatingRange = cache.selectedRatingRange;
        this.reportData = cache.reportData;
        this.locationMonthlyDataMap = cache.locationMonthlyDataMap || {};
        this.lastGeneratedAt = this.formatTimeAgo(cache.lastGeneratedAt);
        this.cacheExpiresAt = cache.expiresAt;


        // Ensure data visualization happens when loading from cache
        this.$nextTick(() => {
          this.updateRatingTrendData();
          this.generateTopKeywords();
          this.generateTopStaffMentions();
          this.generateLocationInsights();
          this.updateMonthlyTableData();
        });

        // Setup refresh timer for cache expiration
        this.setupCacheRefreshTimer();

        // Start background refresh if cache is expired
        if (this.isCacheExpired()) {
          this.refreshInBackground();
        }

        return true;
      } catch (error) {
        console.error("Error loading from cache:", error);
        return false;
      }
    },

    getLocationSentimentVuetifyColor(score) {
      if (score === null || score === undefined) return "grey"; // Default for undefined scores
      if (score >= 8) return "success"; // Excellent (e.g., green)
      if (score >= 6) return "blue"; // Good (e.g., blue)
      if (score >= 4) return "warning"; // Average (e.g., amber/orange)
      return "error"; // Needs Attention (e.g., red)
    },

    handleMonthChange(value) {
      this.selectedMonth = value;
      // Reset to page 1 for any paginated content
      this.currentPage = 1;

      // Update visualizations
      this.$nextTick(() => {
        this.updateRatingTrendData();
        this.generateTopKeywords();
        this.generateTopStaffMentions();
        this.generateLocationInsights();
        this.updateMonthlyTableData();
      });
    },

    // Update available months whenever report data changes
    updateAvailableMonths() {
      if (!this.reportData?.data) {
        this.availableMonths = [...this.monthOptions];
        return;
      }

      const monthItems = Object.keys(this.reportData.data)
        .sort((a, b) => new Date(b) - new Date(a))
        .map((month) => ({
          text: this.formatMonth(month),
          value: month,
        }));

      this.availableMonths = [...this.monthOptions, ...monthItems];
    },

    // Fetch keyword/staff reviews for a specific location
    async fetchKeywordReviews(locationData, keyword) {
      console.log(
        "These are input values ",
        locationData,
        keyword,
        this.paginatedLocations
      );
      if (!locationData || !keyword) {
        console.error("Missing location data or keyword");
        return;
      }

      // Determine if this is a staff member or keyword based on the type
      const isStaffMember = keyword.type === 'staff';
      
      if (isStaffMember) {
        // For staff members, call showStaffReviews with modified staff object and specific locationId
        const staffObject = {
          name: keyword.text,
          reviewIds: locationData.reviews || [],
          count: locationData.count || 0
        };
        this.showStaffReviews(staffObject, locationData.id); // Pass the specific locationId
        return;
      }

      // Original keyword review logic
      this.isLoadingKeywordReviews = true;
      this.selectedKeywordForReviews = keyword;
      this.selectedLocationName = this.findLocationName(locationData.id);
      this.showKeywordReviewsModal = true;
      this.keywordReviews = [];

      try {
        // Get the location ID for the API call
        const locationId = locationData.id;

        // Extract reviews array from keyword data
        // The reviews array should be available in the keyword object
        let reviews = Array.isArray(locationData.reviews)
          ? locationData.reviews
          : [];
        if (reviews.length === 0 && Array.isArray(keyword.reviews)) {
          reviews = keyword.reviews;
        }

        if (reviews.length === 0) {
          console.warn("Reviews array is empty");
          this.keywordReviews = [];
          this.isLoadingKeywordReviews = false;
          return;
        }

        // Prepare the payload for the API call
        const payload = {
          action: "get_keyword_reviews",
          location: locationId,
          reviewIds: reviews,
        };

        // Get auth token
        const idToken = await auth.currentUser.getIdToken();

        // Make the API call
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log("Keyword reviews response:", result);

          if (result.success && result.data && result.data.reviews) {
            this.keywordReviews = result.data.reviews;
          } else {
            console.error("Invalid response format:", result);
            this.keywordReviews = [];
          }
        } else {
          console.error("API request failed with status:", response.status);
          const errorText = await response.text();
          console.error("Error response:", errorText);
          this.keywordReviews = [];
          this.$alert?.show(`Failed to fetch reviews: ${response.status}`);
        }
      } catch (error) {
        console.error("Error fetching keyword reviews:", error);
        this.keywordReviews = [];
        this.$alert?.show("Failed to fetch keyword reviews: " + error.message);
      } finally {
        this.isLoadingKeywordReviews = false;
      }
    },

    // Show reviews for a specific staff member
    async showStaffReviews(staff, specificLocationId = null) {
      console.log("Opening staff reviews for:", staff, "at location:", specificLocationId);

      // Validate staff object
      if (!staff || !staff.name) {
        console.error("Invalid staff object:", staff);
        this.$alert?.show("Invalid staff member data");
        return;
      }

      this.isLoadingStaffReviews = true;
      this.selectedStaffForReviews = staff;
      this.showStaffReviewsModal = true;
      this.staffReviews = [];

      try {
        // Get review IDs from staff mention with validation
        const reviewIds = Array.isArray(staff.reviewIds) ? staff.reviewIds : [];

        if (reviewIds.length === 0) {
          console.warn("No review IDs found for staff:", staff.name);
          this.staffReviews = [];
          this.isLoadingStaffReviews = false;
          return;
        }

        // Determine location ID - use specific location if provided, otherwise fallback
        let locationId = specificLocationId;
        
        if (specificLocationId) {
          console.log(`✅ Using specific location ID: ${specificLocationId}`);
        } else {
          console.log("🔄 No specific location provided, using fallback logic");
        }
        
        if (!locationId) {
          // For staff reviews, we need to provide a location ID
          // Since staff mentions can span multiple locations, we'll use the first selected location
          // or fall back to the first available location
          if (this.selectedWIDs.length > 0) {
            // Use the first selected location
            const firstSelectedStore = this.stores.find(store => 
              this.selectedWIDs.includes(store.id) || this.selectedWIDs.includes(store.wid)
            );
            locationId = firstSelectedStore?.locationId || firstSelectedStore?.placeId;
          }
          
          if (!locationId && this.stores.length > 0) {
            // Fallback to first available location if no selection
            locationId = this.stores[0]?.locationId || this.stores[0]?.placeId;
          }
        }
        
        if (!locationId) {
          console.error("No location ID available for staff reviews");
          this.$alert?.show("Unable to load staff reviews - no location available");
          this.isLoadingStaffReviews = false;
          return;
        }

        // Prepare the payload for the API call - same structure as fetchKeywordReviews
        const payload = {
          action: "get_keyword_reviews",
          location: locationId,
          reviewIds: reviewIds,
        };

        console.log("Staff reviews payload:", payload);

        // Get authentication token
        const idToken = await auth.currentUser.getIdToken();

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        // Make the API call
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (response.ok) {
          const result = await response.json();
          console.log("Staff reviews response:", result);

          if (result.success && result.data && result.data.reviews) {
            this.staffReviews = result.data.reviews;
          } else {
            console.error("Invalid response format:", result);
            this.staffReviews = [];
          }
        } else {
          console.error("API request failed with status:", response.status);
          const errorText = await response.text();
          console.error("Error response:", errorText);
          this.staffReviews = [];
          this.$alert?.show(`Failed to fetch reviews: ${response.status}`);
        }
      } catch (error) {
        console.error("Error fetching staff reviews:", error);
        this.staffReviews = [];
        
        if (error.name === 'AbortError') {
          this.$alert?.show("Request timed out. Please try again.");
        } else {
          this.$alert?.show("Failed to fetch staff reviews: " + error.message);
        }
      } finally {
        this.isLoadingStaffReviews = false;
      }
    },

    // Format review date for display
    formatReviewDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("Error formatting date:", error);
        return dateString;
      }
    },

    // Convert star rating from text to number
    getStarRating(starRating) {
      const ratingMap = {
        ONE: 1,
        TWO: 2,
        THREE: 3,
        FOUR: 4,
        FIVE: 5,
      };

      return ratingMap[starRating] || 0;
    },

    // Clean up staff reviews modal data
    closeStaffReviewsModal() {
      this.showStaffReviewsModal = false;
      // Clean up data to prevent stale information
      this.staffReviews = [];
      this.selectedStaffForReviews = null;
      this.isLoadingStaffReviews = false;
      // Reset pagination and sorting to defaults
      this.reviewsCurrentPage = 1;
      this.reviewsSortField = "createTime";
      this.reviewsSortOrder = "desc";
      this.reviewsPerPage = 5;
    },

    // Clean up location modal data
    closeLocationModal() {
      this.showLocationModal = false;
      // Clear search input and reset pagination
      this.keywordLocationSearch = "";
      this.currentPage = 1;
      // Clear selected data
      this.selectedLocations = [];
      this.selectedKeyword = null;
    },

    // Clean up keyword reviews modal data
    closeKeywordReviewsModal() {
      this.showKeywordReviewsModal = false;
      // Clear reviews data and reset pagination
      this.keywordReviews = [];
      this.selectedKeywordForReviews = null;
      this.selectedLocationName = "";
      this.reviewsCurrentPage = 1;
      // Reset sorting to defaults
      this.reviewsSortField = "createTime";
      this.reviewsSortOrder = "desc";
      this.reviewsPerPage = 5;
    },

    // Debug method to check staff location mapping
    debugStaffLocationMap() {
      console.log("=== STAFF LOCATION MAP DEBUG ===");
      console.log("reportData exists:", !!this.reportData);
      console.log("staffLocationMap exists:", !!this.reportData?.staffLocationMap);
      console.log("staffLocationMap structure:", this.reportData?.staffLocationMap);
      if (this.reportData?.staffLocationMap) {
        Object.entries(this.reportData.staffLocationMap).forEach(([month, staffMap]) => {
          console.log(`Month: ${month}`);
          Object.entries(staffMap).forEach(([staffName, locations]) => {
            console.log(`  Staff: ${staffName} -> ${locations.length} locations`);
            locations.forEach((loc, idx) => {
              console.log(`    Location ${idx + 1}: ${loc.id} (count: ${loc.count})`);
            });
          });
        });
      }
      console.log("=== END DEBUG ===");
    },
  },

  async created() {
    this.generateDateOptions();
  },

  mounted() {
    // Fetch workspace data
    this.fetchWorkspaceData();

    // Ensure visualizations are rendered once component is mounted
    if (this.reportData?.data) {
      this.$nextTick(() => {
        this.fetchStores();
        this.updateRatingTrendData();
        this.generateTopKeywords();
        this.generateTopStaffMentions();
        this.generateLocationInsights();
        this.updateMonthlyTableData(); // Initialize monthly table data
      });
    }
    document.addEventListener("click", this.handleClickOutside);

    // Set isInitialMount to false after a short delay to allow initial values to stabilize
    setTimeout(() => {
      this.isInitialMount = false;
    }, 1000);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);

    // Clear any intervals
    if (this.cacheRefreshInterval) {
      clearInterval(this.cacheRefreshInterval);
    }
  },

  watch: {
    keywordLocationSearch() {
      this.currentPage = 1; // Reset to first page when search changes
    },

    // Update trend and keywords whenever report data changes
    "reportData.data": {
      handler() {
        if (this.reportData?.data) {
          this.updateAvailableMonths();
          this.updateRatingTrendData();
          this.generateTopKeywords();
          this.generateTopStaffMentions();
          // Location insights are generated after report generation or cache load.
          // If reportData changes due to background refresh, ensure insights are updated.
          if (!this.isInitialMount) {
            // Avoid re-generating during initial load if already done
            this.generateLocationInsights();
          }
        }
      },
      deep: true,
    },

    // Watch for changes in the effective data
    effectiveReportData: {
      handler() {
        if (this.effectiveReportData?.data) {
          this.$nextTick(() => {
            this.updateRatingTrendData();
            this.generateTopKeywords();
            this.generateTopStaffMentions();
            this.generateLocationInsights();
            this.updateMonthlyTableData();
          });
        }
      },
      deep: true,
    },

    // Auto-generate report when selected locations change
    selectedWIDs: {
      handler(newVal, oldVal) {
        // Only trigger if it's a meaningful change (not initialization)
        if (
          !this.isInitialMount &&
          oldVal &&
          newVal !== oldVal &&
          !this.isGenerating
        ) {
          const newSet = new Set(newVal);
          const oldSet = new Set(oldVal);
          // check if new set and old set contains the same values
          if (
            newSet.size !== oldSet.size ||
            ![...newSet].every((id) => oldSet.has(id))
          ) {
            this.generateReport();
          }
        }
      },
      deep: true,
    },
    globalStores: {
      immediate: true,
      async handler(newStores) {
        if (newStores && newStores.length > 0) {
          await this.fetchStores();
          const loadedFromCache = this.loadFromCache(); // Tries to load reportData, selectedWIDs etc.

          if (loadedFromCache) {
            // If loaded from cache, visualizations are updated in loadFromCache's nextTick.
            // Ensure this.stores is correctly populated for generateLocationInsights.
            // fetchStores has already run.
            if (this.isCacheExpired()) {
              this.refreshInBackground();
            }
          } else {
            // Not loaded from cache, generate a new report.
            // generateReport will call generateLocationInsights internally after reportData is ready.
            if (
              (this.stores && this.stores.length > 0) ||
              this.$route.query.location
            ) {
              // Check if stores are populated or a location is in URL
              await this.generateReport();
            } else {
              console.warn(
                "GMBReviewInsights: No stores to generate report for, or globalStores yielded no applicable stores. Report generation skipped."
              );
              // Display a message or handle empty state if necessary
            }
          }
          this.hasInitializedWithGlobalStores = true;
        } else if (
          (!newStores || newStores.length === 0) &&
          !this.hasInitializedWithGlobalStores
        ) {
          console.log(
            "GMBReviewInsights: globalStores watcher: globalStores is empty or not yet available."
          );
        }
      },
    },

    // Auto-generate report when rating filter changes
    selectedRatingRange(newVal, oldVal) {
      if (
        !this.isInitialMount &&
        oldVal !== undefined &&
        newVal !== oldVal &&
        !this.isGenerating
      ) {
        this.generateReport();
      }
    },

    // Watch for report data changes to update available months
    "reportData.data": {
      handler() {
        if (this.reportData?.data) {
          this.updateAvailableMonths();
          this.updateRatingTrendData();
          this.generateTopKeywords();
          this.generateTopStaffMentions();
          // Location insights are generated after report generation or cache load
          if (!this.isInitialMount) {
            this.generateLocationInsights();
          }
        }
      },
      deep: true,
    },

    // Watch for changes in the effective data
    effectiveReportData: {
      handler() {
        if (this.effectiveReportData?.data) {
          this.$nextTick(() => {
            this.updateRatingTrendData();
            this.generateTopKeywords();
            this.generateTopStaffMentions();
            this.generateLocationInsights();
            this.updateMonthlyTableData();
          });
        }
      },
      deep: true,
    },

    // Clear selected month when rating range changes
    selectedRatingRange() {
      this.selectedMonth = null;
      if (!this.isInitialMount && !this.isGenerating) {
        this.generateReport();
      }
    },
  },
};
</script>

<style scoped>
.v-select {
  max-width: none !important;
}

@media (min-width: 640px) {
  .v-select {
    max-width: 300px !important;
  }
}

/* Ensure consistent height for select inputs and button */
:deep(.v-input__control) {
  min-height: 40px !important;
}

:deep(.v-input__slot) {
  min-height: 40px !important;
}

/* Remove bottom margin from selects */
:deep(.v-text-field__details) {
  display: none;
}

/* Location dropdown styles */
.location-dropdown {
  position: relative;
}

.location-dropdown input {
  background: transparent;
}

.location-dropdown .relative:hover {
  border-color: #a0aec0;
}

.location-dropdown .relative:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.5);
}

/* Add this to ensure proper table scroll on mobile */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.overflow-x-auto {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

@media (max-width: 768px) {
  .table-container {
    display: none;
  }
}

/* Button consistency */
:deep(.v-btn) {
  text-transform: none !important;
}

/* Ensure buttons stack properly on mobile */
@media (max-width: 640px) {
  .v-btn {
    width: 100%;
  }
}

.location-dropdown .v-progress-circular {
  margin: 0 4px;
}

.rating-filter {
  min-width: 150px;
}

/* Ensure consistent height with other filters */
:deep(.rating-filter .v-input__control) {
  min-height: 40px !important;
}

:deep(.rating-filter .v-input__slot) {
  min-height: 40px !important;
}

/* Add these new styles */
.rating-filter {
  max-width: 200px !important;
}

:deep(.rating-filter .v-input__slot) {
  cursor: pointer !important;
}

:deep(.rating-filter.v-input--is-disabled .v-input__slot) {
  cursor: not-allowed !important;
  opacity: 0.7;
}

/* Add notification transition styles */
.transition-opacity {
  transition: opacity 0.5s ease-in-out;
}

/* Rating trend styles */
.rating-trend {
  position: relative;
}

.rating-trend svg {
  overflow: visible;
}

.rating-trend .grid-lines line {
  stroke-dasharray: 4 4;
}

.rating-trend circle {
  transition: r 0.2s ease-in-out;
}

.rating-trend circle:hover {
  r: 6;
  cursor: pointer;
}

.rating-trend text {
  font-family: system-ui, -apple-system, sans-serif;
}

/* Ensure trend points and line are above grid */
.rating-trend polyline,
.rating-trend circle {
  z-index: 2;
  position: relative;
}

/* Add smooth animation for trend line */
.rating-trend polyline {
  transition: all 0.3s ease-in-out;
}

/* Custom styles for preview modal to avoid left panel overlap */
.preview-modal-custom {
  margin-left: 280px !important;
  width: calc(100% - 300px) !important;
  max-width: calc(100vw - 320px) !important;
}

/* Ensure modal dialog is properly positioned */
:deep(.v-dialog__container) {
  justify-content: flex-end !important;
  padding-left: 280px !important;
}

/* Override dialog content positioning */
:deep(.v-dialog.preview-modal-custom) {
  margin: 0 !important;
  margin-left: auto !important;
  margin-right: 20px !important;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 960px) {
  .preview-modal-custom {
    margin-left: 60px !important;
    width: calc(100% - 80px) !important;
    max-width: calc(100vw - 80px) !important;
  }
  
  :deep(.v-dialog__container) {
    padding-left: 60px !important;
  }
}
</style>
